/**
 * WorldCupDisplayPage — Full-screen World Cup hub for 40" touchscreen
 * Route: /wc2026  (ProtectedRoute, no AppLayout)
 *
 * Matches Racewise CSS variables, Podium branding.
 * Countdown lives inline in the tab bar.
 * Fixtures use the same horizontal-scroll carousel as FootballHubPage.
 * 60 s inactivity on any sub-tab → revert to Home.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { setWcTeamIds, isWcTeam, teamAbbr } from '../lib/wcTeams';
import { PlayerProfileModal } from '../components/PlayerProfileModal';
import BracketMatchModal from '../components/BracketMatchModal';
import { Tooltip, TooltipTrigger, TooltipContent } from '../components/ui/tooltip';
import { useKioskInactivity } from '../hooks/useKioskInactivity';
import { usePlayerPhoto } from '../lib/usePlayerPhoto';
import { useAuth } from '../context/AuthContext';

const API_URL    = process.env.REACT_APP_BACKEND_URL;
const WC_OPENS   = new Date('2026-06-11T19:00:00.000Z'); // Opening match ~20:00 BST / 15:00 EDT
const WC_COMP_ID = 700; // default; overridden by site settings at runtime
const PODIUM_LOGO = 'https://podiumsports.com/wp-content/uploads/2025/01/Poduim-Logo.png';
const WC_LOGO    = 'https://upload.wikimedia.org/wikipedia/en/thumb/1/17/2026_FIFA_World_Cup_emblem.svg/960px-2026_FIFA_World_Cup_emblem.svg.png';

// Carousel card dimensions — mirror FootballHubPage constants
const CARD_W   = 176;
const CARD_GAP = 12;

function authH() { return { Authorization: `Bearer ${getToken()}` }; }

// ── news helpers (identical to FootballHubPage) ───────────────────────────────

function extractArticleId(item) {
  if (item.uri) return item.uri;
  try {
    const href = item.links?.[0]?.href || '';
    const segs = href.split('/');
    return segs[segs.length - 1].split('?')[0];
  } catch { return null; }
}
function itemImage(item, rendition = '4x3') {
  return item?.associations?.featureimage?.renditions?.[rendition]?.href || null;
}
function fmtIssued(iso) {
  if (!iso) return '';
  try { return format(parseISO(iso), 'd MMM yyyy, HH:mm'); }
  catch { return iso; }
}

// ── social embed processing (identical to FootballHubPage) ───────────────────

function processArticleBody(html) {
  if (!html) return [];
  let h = html
    .replace(/\\u003C/g, '<').replace(/\\u003E/g, '>')
    .replace(/\\u0026/g, '&').replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"');

  // Strip Kaltura video embeds — PA's player SDK isn't available to us,
  // so remove the player div and its aspect-ratio wrapper to avoid blank boxes.
  h = h.replace(/<div[^>]*\bid="kaltura_player_[^"]*"[^>]*>[\s\S]*?<\/div>/gi, '');
  h = h.replace(/<div[^>]*style="[^"]*padding-bottom\s*:\s*\d+(?:\.\d+)?%[^"]*"[^>]*>\s*<\/div>/gi, '');

  // Strip StreamAMG video embeds — PA's streaming player, renders as blank space on our domain.
  // Matches the wrapper div + the loader script tag that follows it.
  h = h.replace(/<div[^>]*style="[^"]*display\s*:\s*inline-block[^"]*"[^>]*>[\s\S]*?<\/div>\s*<script[^>]*streamamg[^>]*><\/script>/gi, '');
  // Also strip any remaining streamamg script tags
  h = h.replace(/<script[^>]*streamamg[^>]*>[\s\S]*?<\/script>/gi, '');

  // Strip PA's injected Google Analytics tracking blocks — they fire on our domain
  // and leak PA internal dimension data; silently remove them.
  h = h.replace(/<!--\s*TRACKING START GA\s*-->[\s\S]*?<!--\s*TRACKING END GA\s*-->/gi, '');

  // Extract Flourish embeds before segment splitting so the script tag doesn't get swallowed
  h = h.replace(/<div[^>]*class="[^"]*flourish-embed[^"]*"[^>]*data-src="([^"]+)"[^>]*>[\s\S]*?<\/div>/gi,
    (_, dataSrc) => `<!--FLOURISH:${dataSrc}-->`);

  const segments = [];
  const combinedRe = /<!--\s*EMBED START (Twitter|X|Instagram)\s*-->([\s\S]*?)<!--\s*EMBED END \1\s*-->|<!--FLOURISH:([^>]+?)-->/gi;
  let lastIndex = 0, match;
  while ((match = combinedRe.exec(h)) !== null) {
    if (match.index > lastIndex) segments.push({ type: 'html', content: h.slice(lastIndex, match.index) });
    if (match[3] !== undefined) {
      segments.push({ type: 'flourish', dataSrc: match[3] });
    } else {
      const platform = match[1].toLowerCase();
      if (platform === 'twitter' || platform === 'x') {
        segments.push({ type: 'tweet', ...parseTweet(match[2]) });
      } else {
        const urlM = match[2].match(/href="([^"]*instagram\.com[^"]*)"/i);
        segments.push({ type: 'instagram', url: urlM?.[1] || '' });
      }
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < h.length) segments.push({ type: 'html', content: h.slice(lastIndex) });
  return segments.length ? segments : [{ type: 'html', content: h }];
}

function parseTweet(embedHtml) {
  const bqM = embedHtml.match(/<blockquote[^>]*>([\s\S]*?)<\/blockquote>/i);
  if (!bqM) return { text: '', url: '' };
  const inner = bqM[1];
  const paras = [];
  const pRe = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  let m;
  while ((m = pRe.exec(inner)) !== null) paras.push(m[1]);
  const lastPara = paras[paras.length - 1] || '';
  const decode = (s) => { const t = document.createElement('textarea'); t.innerHTML = s; return t.value; };
  const tweetText = decode(paras.slice(0, -1).map(p => p.replace(/<[^>]+>/g, '')).join(' ').trim());
  const urlM = lastPara.match(/href="(https?:\/\/(?:twitter|x)\.com\/[^/]+\/status\/(\d+)[^"]*)"/i);
  return { text: tweetText, url: urlM?.[1] || '' };
}

function FlourishEmbed({ dataSrc }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!dataSrc || !ref.current) return;
    if (window.Flourish) { window.Flourish.reload?.(); return; }
    if (!document.getElementById('flourish-embed-js')) {
      const s = document.createElement('script');
      s.id = 'flourish-embed-js';
      s.src = 'https://public.flourish.studio/resources/embed.js';
      s.async = true;
      document.head.appendChild(s);
    }
  }, [dataSrc]);
  if (!dataSrc) return null;
  return (
    <div ref={ref} className="flourish-embed" data-src={dataSrc} style={{ width: '100%', minHeight: 400 }} />
  );
}

function TweetCard({ url, text }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!url || !ref.current) return;
    const load = () => window.twttr?.widgets?.load(ref.current);
    if (window.twttr?.widgets) { load(); return; }
    if (!document.getElementById('twitter-widgets-js')) {
      const s = document.createElement('script');
      s.id = 'twitter-widgets-js'; s.src = 'https://platform.twitter.com/widgets.js';
      s.async = true; s.charset = 'utf-8'; s.onload = load;
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.twttr?.widgets) { clearInterval(t); load(); } }, 100);
      setTimeout(() => clearInterval(t), 5000);
    }
  }, [url]);
  if (!url) return null;
  return (
    <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center relative">
      <blockquote className="twitter-tweet" data-theme="dark" data-dnt="true">
        <a href={url}>{text || 'View tweet'}</a>
      </blockquote>
      {/* Kiosk overlay — absorbs all taps on the iframe so users can't navigate away */}
      <div className="absolute inset-0 z-10" />
    </div>
  );
}

function InstagramCard({ url }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!url || !ref.current) return;
    const load = () => window.instgrm?.Embeds?.process();
    if (window.instgrm?.Embeds) { load(); return; }
    if (!document.getElementById('instagram-embed-js')) {
      const s = document.createElement('script');
      s.id = 'instagram-embed-js';
      s.src = 'https://www.instagram.com/embed.js';
      s.async = true; s.onload = load;
      document.head.appendChild(s);
    } else {
      const t = setInterval(() => { if (window.instgrm?.Embeds) { clearInterval(t); load(); } }, 100);
      setTimeout(() => clearInterval(t), 5000);
    }
  }, [url]);
  if (!url) return null;
  // Strip query params and ensure trailing slash — required by Instagram embed API
  const embedUrl = url.split('?')[0].replace(/\/?$/, '/');
  return (
    <div ref={ref} className="my-3 max-w-lg mx-auto flex justify-center relative">
      <blockquote
        className="instagram-media"
        data-instgrm-permalink={embedUrl}
        data-instgrm-version="14"
        data-instgrm-captioned
        style={{ background: '#FFF', border: 0, borderRadius: 3, minWidth: 326, width: 'calc(100% - 2px)' }}
      >
        <a href={embedUrl}>View on Instagram</a>
      </blockquote>
      {/* Kiosk overlay — absorbs all taps on the iframe */}
      <div className="absolute inset-0 z-10" />
    </div>
  );
}

function ArticleBody({ html }) {
  const segments = useMemo(() => processArticleBody(html), [html]);
  return (
    <div>
      {segments.map((seg, i) => {
        if (seg.type === 'tweet') return <TweetCard key={i} {...seg} />;
        if (seg.type === 'instagram') return <InstagramCard key={i} url={seg.url} />;
        if (seg.type === 'flourish') return <FlourishEmbed key={i} dataSrc={seg.dataSrc} />;
        return (
          <div key={i}
            className="prose prose-sm dark:prose-invert max-w-none text-foreground/80 leading-relaxed article-body"
            dangerouslySetInnerHTML={{ __html: seg.content }} />
        );
      })}
    </div>
  );
}

// ── misc helpers ──────────────────────────────────────────────────────────────

function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}
function fmtMatchDay(yyyymmdd) {
  if (!yyyymmdd) return '';
  const d = new Date(`${yyyymmdd.slice(0,4)}-${yyyymmdd.slice(4,6)}-${yyyymmdd.slice(6,8)}T12:00:00Z`);
  if (d.toDateString() === new Date().toDateString()) return 'Today';
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}
function fmtKickoff(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function useCountdown(target) {
  const [diff, setDiff] = useState(target - Date.now());
  useEffect(() => {
    const t = setInterval(() => setDiff(target - Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  const total = Math.max(0, Math.floor(diff / 1000));
  return {
    days:    Math.floor(total / 86400),
    hours:   Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
    over:    diff <= 0,
  };
}

// ── shared atoms ──────────────────────────────────────────────────────────────

function TeamBadge({ teamId, teamName, size = 24 }) {
  const [err, setErr] = useState(false);
  if (!err && teamId) {
    return (
      <img src={badgeUrl(teamId)} alt={teamName || ''}
        style={{ width: size, height: size }}
        className="object-contain flex-shrink-0"
        onError={() => setErr(true)} />
    );
  }
  return (
    <div style={{ width: size, height: size }}
      className="rounded-full bg-muted flex items-center justify-center flex-shrink-0
                 text-muted-foreground font-bold text-xs">
      {(teamName || '?')[0]}
    </div>
  );
}

// ── Team Intel tooltip (FIFA ranking snapshot + recent form) ─────────────────
// Cache fetched intel in module scope so we only hit the API once per team per session
const _intelCache = {};

function TeamIntelTooltip({ teamId, teamName, size = 22, children }) {
  const [intel, setIntel] = useState(_intelCache[teamId] || null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const fetchIntel = async () => {
    if (!teamId || intel || loading) return;
    if (_intelCache[teamId]) { setIntel(_intelCache[teamId]); return; }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/football/worldcup/team-intel/${teamId}`,
        { headers: authH() }
      );
      _intelCache[teamId] = res.data;
      setIntel(res.data);
    } catch {
      _intelCache[teamId] = null; // mark as not available so we don't retry
    } finally {
      setLoading(false);
    }
  };

  const rankDiff = intel ? (intel.previous_rank - intel.rank) : 0;

  const tooltipBody = (
    <div className="min-w-[200px] space-y-2.5">
      {/* Header */}
      <div className="flex items-center gap-2">
        <TeamBadge teamId={teamId} teamName={teamName} size={28} />
        <div>
          <p className="font-bold text-sm leading-tight">{intel?.pa_team_name || teamName}</p>
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{intel?.confederation || ''}</p>
        </div>
      </div>

      {loading && <p className="text-xs text-muted-foreground">Loading...</p>}

      {intel && (
        <>
          {/* Rank */}
          <div className="flex items-center justify-between border-t border-border/50 pt-2">
            <span className="text-xs text-muted-foreground">FIFA Ranking</span>
            <div className="flex items-center gap-1.5">
              <span className="text-lg font-black leading-none">#{intel.rank}</span>
              {rankDiff !== 0 && (
                <span className={`text-[10px] font-bold ${rankDiff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {rankDiff > 0 ? `▲${rankDiff}` : `▼${Math.abs(rankDiff)}`}
                </span>
              )}
            </div>
          </div>

          {/* Recent form */}
          {intel.recent_matches?.length > 0 && (
            <div className="border-t border-border/50 pt-2 space-y-1">
              <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">Recent Results</p>
              {intel.recent_matches.slice(0, 5).map((m, i) => {
                const isHome = m.home_code === intel.country_code;
                const teamScore  = isHome ? m.home_score : m.away_score;
                const oppScore   = isHome ? m.away_score : m.home_score;
                const oppName    = isHome ? m.away : m.home;
                const oppCode    = isHome ? m.away_code : m.home_code;
                const result = teamScore > oppScore ? 'W' : teamScore < oppScore ? 'L' : 'D';
                const resultColor = result === 'W' ? 'text-green-400' : result === 'L' ? 'text-red-400' : 'text-amber-400';
                return (
                  <div key={i} className="flex items-center justify-between gap-2 text-xs">
                    <div className="flex items-center gap-1 min-w-0">
                      <span className={`font-black w-4 flex-shrink-0 ${resultColor}`}>{result}</span>
                      <span className="text-muted-foreground truncate">{oppName}</span>
                    </div>
                    <span className="font-bold tabular-nums flex-shrink-0">{teamScore}–{oppScore}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pre-tournament label */}
          <p className="text-[9px] font-semibold text-muted-foreground/60 uppercase tracking-widest border-t border-border/30 pt-1.5">
            Pre-tournament data
          </p>
        </>
      )}

      {!loading && !intel && (
        <p className="text-xs text-muted-foreground border-t border-border/50 pt-2">No data available</p>
      )}
    </div>
  );

  const handleOpen = () => { fetchIntel(); setOpen(true); };

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger
        asChild
        onMouseEnter={handleOpen}
        onFocus={handleOpen}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : handleOpen(); }}
      >
        <span className="cursor-pointer">{children}</span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="bg-card text-foreground border border-border shadow-xl text-xs p-3 rounded-xl"
      >
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}

function PlayerPhoto({ playerId, playerName, size = 56 }) {
  const [err, setErr] = useState(false);

  if (!playerId || playerId === '0' || err) {
    return (
      <div style={{ width: size, height: size, fontSize: size * 0.38 }}
        className="rounded-full bg-muted border border-border flex items-center
                   justify-center flex-shrink-0 text-muted-foreground font-bold">
        {(playerName || '?')[0]}
      </div>
    );
  }
  const src = wcPlayerUrl(playerId);
  return (
    <div style={{
        width: size, height: size,
        backgroundImage: `url(${src})`,
        backgroundSize: 'auto 230%',
        backgroundPosition: '50% 5%',
        backgroundRepeat: 'no-repeat',
      }}
      className="rounded-full border border-border flex-shrink-0 bg-muted"
      role="img" aria-label={playerName || ''}>
      <img src={src} alt="" className="sr-only" onError={() => setErr(true)} />
    </div>
  );
}

function SectionHead({ label }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="w-1 h-5 rounded-full bg-primary inline-block flex-shrink-0" />
      <h2 className="text-foreground font-bold text-base">{label}</h2>
    </div>
  );
}

function EmptyState({ icon, title, sub }) {
  return (
    <div className="h-full flex flex-col items-center justify-center text-center px-8 space-y-3">
      <span className="text-5xl">{icon}</span>
      <p className="text-foreground font-bold text-xl">{title}</p>
      <p className="text-muted-foreground text-sm">{sub}</p>
    </div>
  );
}

// ── COUNTDOWN — compact inline chip for the tab bar ──────────────────────────

// ── COUNTDOWN PANEL — large card for the stats slot before tournament starts ──

function CountdownPanel({ title }) {
  const { days, hours, minutes, seconds, over } = useCountdown(WC_OPENS.getTime());
  if (over) return null;

  const units = [
    { value: days,    label: 'DAYS' },
    { value: hours,   label: 'HRS'  },
    { value: minutes, label: 'MIN'  },
    { value: seconds, label: 'SEC'  },
  ];

  const kickoffDate = WC_OPENS.toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="h-full flex flex-col items-center justify-center rounded-xl border border-border/40
                    bg-card px-6 py-6 text-center gap-5"
         style={{ background: 'linear-gradient(145deg, #0a0a1a 0%, #12002a 100%)' }}>
      {/* Logo + title */}
      <div className="flex flex-col items-center gap-3">
        <img src={WC_LOGO} alt="FIFA World Cup 2026"
             className="h-20 w-20 object-contain drop-shadow-lg" />
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">
          {title || 'FIFA World Cup 2026'}
        </p>
        <p className="text-sm font-black uppercase tracking-widest" style={{ color: '#ff1654' }}>
          Kicks Off In
        </p>
      </div>

      {/* Number tiles */}
      <div className="grid grid-cols-4 gap-3 w-full">
        {units.map(({ value, label }) => (
          <div key={label}
               className="flex flex-col items-center rounded-2xl py-4 px-2"
               style={{ backgroundColor: '#ff1654' }}>
            <span className="text-4xl font-black tabular-nums leading-none text-white">
              {String(value).padStart(2, '0')}
            </span>
            <span className="text-[10px] font-bold tracking-widest mt-1.5 text-white/80">
              {label}
            </span>
          </div>
        ))}
      </div>

      {/* Date */}
      <p className="text-xs text-white/40 font-medium">{kickoffDate}</p>
    </div>
  );
}

// ── FIXTURE CARD — identical layout to FootballHubPage ────────────────────────

function FixtureCard({ match: m, anchor, onClick }) {
  const isLive     = m.status === 'live';
  const isFinished = m.status === 'finished' || m.status === 'ft' ||
                     (m.home_score != null && m.away_score != null && !isLive && m.status !== 'scheduled');
  const hasScore   = m.home_score != null && m.away_score != null;

  return (
    <button
      onClick={() => onClick?.(m)}
      className={`flex-shrink-0 w-44 rounded-xl border p-3 bg-card transition-colors cursor-pointer select-none text-left
        ${isLive   ? 'border-green-500/50 bg-green-500/5' : ''}
        ${anchor   ? 'ring-2 ring-primary/40' : 'hover:bg-muted/40'}
        ${!isLive && !anchor ? 'border-border' : ''}`}
    >
      {/* Date / time / status */}
      <div className="mb-2.5 space-y-0.5">
        <div className="flex items-center justify-between gap-1">
          <span className="text-xs text-muted-foreground leading-none">
            {fmtMatchDay(m.match_date)}
            {!isFinished && !isLive && m.kickoff_time ? `, ${fmtKickoff(m.kickoff_time)}` : ''}
          </span>
          {isLive && (
            <span className="flex items-center gap-1 text-xs font-semibold text-green-500">
              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse inline-block" />
              {m.phase_label || 'Live'}
            </span>
          )}
          {isFinished && !isLive && (
            <span className="text-xs text-muted-foreground font-medium">
              {m.phase_label || 'FT'}
            </span>
          )}
          {!isFinished && !isLive && (
            <span className="text-xs text-muted-foreground font-medium">
              {fmtTime(m.kickoff_time)}
            </span>
          )}
        </div>
        {m.round && <p className="text-xs text-muted-foreground truncate">{m.round}</p>}
      </div>

      {/* Home */}
      <div className="flex items-center justify-between gap-1 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
            <TeamIntelTooltip teamId={m.home_team_id} teamName={m.home_team_name} size={22}>
              <TeamBadge teamId={m.home_team_id} teamName={m.home_team_name} size={22} />
            </TeamIntelTooltip>
          <span className={`text-sm font-medium truncate ${isLive ? 'text-green-500' : 'text-foreground'}`}>
            {m.home_team_name}
          </span>
        </div>
        {hasScore && (
          <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${isLive ? 'text-green-500' : 'text-foreground'}`}>
            {m.home_score}
          </span>
        )}
      </div>

      {/* Away */}
      <div className="flex items-center justify-between gap-1">
        <div className="flex items-center gap-1.5 min-w-0">
            <TeamIntelTooltip teamId={m.away_team_id} teamName={m.away_team_name} size={22}>
              <TeamBadge teamId={m.away_team_id} teamName={m.away_team_name} size={22} />
            </TeamIntelTooltip>
          <span className={`text-sm font-medium truncate ${isLive ? 'text-green-500' : 'text-foreground'}`}>
            {m.away_team_name}
          </span>
        </div>
        {hasScore && (
          <span className={`text-sm font-bold tabular-nums flex-shrink-0 ${isLive ? 'text-green-500' : 'text-foreground'}`}>
            {m.away_score}
          </span>
        )}
      </div>
    </button>
  );
}

// ── FIXTURES CAROUSEL — identical mechanics to FootballHubPage ────────────────

function FixturesCarousel({ matches, onOpenMatch }) {
  const scrollRef    = useRef(null);
  const SCROLL_AMOUNT = (CARD_W + CARD_GAP) * 3;

  const scroll = (dir) =>
    scrollRef.current?.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: 'smooth' });

  const sorted = useMemo(() =>
    [...(matches || [])].sort((a, b) =>
      (a.kickoff_time || a.match_date || '').localeCompare(b.kickoff_time || b.match_date || '')
    ), [matches]);

  // Anchor = last live, or last finished, or first upcoming
  const anchorIdx = useMemo(() => {
    const liveIdx = [...sorted].map((m,i) => ({m,i})).filter(({m}) => m.status==='live').pop()?.i;
    if (liveIdx != null) return liveIdx;
    const finIdx  = [...sorted].map((m,i) => ({m,i})).filter(({m}) => m.status==='finished' || m.status==='ft').pop()?.i;
    if (finIdx != null) return finIdx;
    return 0;
  }, [sorted]);

  // Auto-scroll to anchor on load
  useEffect(() => {
    if (!scrollRef.current || !sorted.length) return;
    const el = scrollRef.current;
    const cardOffset  = anchorIdx * (CARD_W + CARD_GAP);
    const centreOffset = el.clientWidth / 2 - CARD_W / 2;
    el.scrollLeft = cardOffset - centreOffset;
  }, [sorted.length, anchorIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!sorted.length) return (
    <p className="text-muted-foreground text-sm py-4">No fixtures available.</p>
  );

  return (
    <div className="flex items-center gap-2">
      <button
        onClick={() => scroll(-1)}
        className="flex-shrink-0 h-8 w-8 rounded-full border border-border bg-card
                   flex items-center justify-center text-muted-foreground
                   hover:text-foreground hover:border-primary/50 transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>

      <div ref={scrollRef}
        className="flex gap-3 overflow-x-auto flex-1"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
        {sorted.map((m, i) => (
          <FixtureCard key={m.pa_match_id} match={m} anchor={i === anchorIdx}
            onClick={onOpenMatch} />
        ))}
      </div>

      <button
        onClick={() => scroll(1)}
        className="flex-shrink-0 h-8 w-8 rounded-full border border-border bg-card
                   flex items-center justify-center text-muted-foreground
                   hover:text-foreground hover:border-primary/50 transition-colors"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

// ── NEWS components (identical structure to FootballHubPage) ──────────────────

function NewsGrid({ items, onOpen, statsSlot }) {
  if (!items?.length) return null;
  const [hero, ...rest] = items;
  const heroImg = itemImage(hero, '4x3');
  const heroId  = extractArticleId(hero);

  return (
    <div className="grid md:grid-cols-5 gap-4">
      {/* Hero — spans 2 rows when stats are present so they fill equal height */}
      <button
        className={`md:col-span-2 ${statsSlot ? 'md:row-span-2' : ''} text-left group rounded-xl overflow-hidden border bg-card
                   hover:border-primary/50 transition-colors`}
        onClick={() => heroId && onOpen(hero)}
      >
        {heroImg ? (
          <div className="aspect-[4/3] overflow-hidden">
            <img src={heroImg} alt={hero.headline}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          </div>
        ) : (
          <div className="aspect-[4/3] bg-muted flex items-center justify-center">
            <span className="text-4xl opacity-30">📰</span>
          </div>
        )}
        <div className="p-4">
          <span className="inline-block bg-red-500 text-white text-[10px] font-bold
                           px-2 py-0.5 rounded mb-2 uppercase">Football</span>
          <h3 className="font-bold text-base leading-snug text-foreground
                         group-hover:text-primary transition-colors line-clamp-3">
            {hero.headline}
          </h3>
          <p className="text-xs text-muted-foreground mt-2">{fmtIssued(hero.issued)}</p>
        </div>
      </button>

      {/* 3 story cards */}
      <div className="md:col-span-3 flex flex-col gap-3">
        {rest.slice(0, 3).map((item) => {
          const img = itemImage(item, '4x3');
          const id  = extractArticleId(item);
          return (
            <button key={id || item.headline}
              onClick={() => id && onOpen(item)}
              className="flex gap-3 text-left group rounded-lg border bg-card p-3
                         hover:border-primary/50 transition-colors">
              {img ? (
                <img src={img} alt={item.headline}
                  className="rounded object-cover flex-shrink-0" style={{ width: 96, height: 72 }} />
              ) : (
                <div className="rounded bg-muted flex-shrink-0 flex items-center justify-center"
                  style={{ width: 96, height: 72 }}>
                  <span className="text-2xl opacity-30">📰</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <span className="inline-block bg-red-500 text-white text-[9px] font-bold
                                 px-1.5 py-0.5 rounded mb-1 uppercase">Football</span>
                <p className="font-semibold text-sm leading-snug text-foreground
                              group-hover:text-primary transition-colors line-clamp-2">
                  {item.headline}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Stats slot — sits in col 3-5, row 2 (directly below the 3 stories) */}
      {statsSlot && (
        <div className="md:col-span-3">
          {statsSlot}
        </div>
      )}
    </div>
  );
}

// ── PANELS ────────────────────────────────────────────────────────────────────

// ── Team picker modal ─────────────────────────────────────────────────────────

function TeamPickerModal({ open, onClose, onSelect, groups, matches, selectedTeamId }) {
  // Placeholder name patterns used for knockout-stage "TBD" entries
  const isPlaceholder = (name) =>
    !name ||
    /^(Winner|Loser|Runner-up|Group [A-Z]\/)/i.test(name) ||
    /^(Winner|Loser|Runner-up)\s+(Group|Match)/i.test(name);

  // Build teams grouped by group name — only real group-stage groups (Group A–L)
  const groupedTeams = useMemo(() => {
    if (groups?.groups?.length) {
      const real = groups.groups
        .filter(g => /^Group [A-L]$/i.test(g.name))          // only Group A … Group L
        .map(g => ({
          groupName: g.name,
          teams: (g.rows || [])
            .filter(r => r.team_id && !isPlaceholder(r.team)) // skip TBD rows
            .map(r => ({ id: String(r.team_id), name: r.team })),
        }))
        .filter(g => g.teams.length > 0);

      if (real.length > 0) return real;
    }
    // Fallback: extract real teams from fixtures (skip TBD team names)
    const seen = new Set();
    const all = [];
    (matches || []).forEach(m => {
      if (m.home_team_id && !seen.has(String(m.home_team_id)) && !isPlaceholder(m.home_team_name)) {
        seen.add(String(m.home_team_id));
        all.push({ id: String(m.home_team_id), name: m.home_team_name });
      }
      if (m.away_team_id && !seen.has(String(m.away_team_id)) && !isPlaceholder(m.away_team_name)) {
        seen.add(String(m.away_team_id));
        all.push({ id: String(m.away_team_id), name: m.away_team_name });
      }
    });
    all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [{ groupName: null, teams: all }];
  }, [groups, matches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flatten + dedupe all teams into a single sorted array
  const allTeams = useMemo(() => {
    const seen = new Set();
    return groupedTeams
      .flatMap(g => g.teams)
      .filter(t => { if (seen.has(t.id)) return false; seen.add(t.id); return true; })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [groupedTeams]);

  // Calculate grid dimensions to fill the screen (target ~3:1 width:height ratio)
  const cols = useMemo(() => {
    const n = allTeams.length;
    if (n === 0) return 12;
    // Try cols 8–16, pick the one whose rows/cols ratio is closest to 1:3 (landscape)
    let best = 12, bestDiff = Infinity;
    for (let c = 8; c <= 16; c++) {
      const r = Math.ceil(n / c);
      const diff = Math.abs(c / r - 3);
      if (diff < bestDiff) { bestDiff = diff; best = c; }
    }
    return best;
  }, [allTeams.length]);
  const rows = Math.ceil(allTeams.length / cols);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      style={{ background: 'rgba(3,7,18,0.98)', backdropFilter: 'blur(12px)', display: 'flex', flexDirection: 'column', height: '100dvh' }}
    >
      {/* Ambient glow blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px]"
             style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.18), transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px]"
             style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.15), transparent 70%)' }} />
        <div className="absolute top-1/2 left-10 w-[300px] h-[300px] rounded-full blur-[80px]"
             style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.10), transparent 70%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-4 flex-shrink-0">
        <div>
          <p className="text-white/35 text-[10px] font-bold uppercase tracking-[0.25em] mb-0.5">
            Fixtures & Results
          </p>
          <h2 className="text-white text-xl font-black tracking-tight">Pick a Team</h2>
        </div>
        <div className="flex items-center gap-3">
          {selectedTeamId && (
            <button
              onClick={() => { onSelect(null); onClose(); }}
              className="text-xs font-semibold text-white/50 hover:text-white border border-white/15
                         hover:border-white/40 px-4 py-2 rounded-full transition-all"
            >
              Clear filter
            </button>
          )}
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center
                       text-white/50 hover:text-white hover:border-white/40 transition-all text-base"
          >
            ✕
          </button>
        </div>
      </div>

      {/* Team grid — fills remaining space exactly, no scroll */}
      <div className="relative z-10 px-4 pb-4" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
        <div
          style={{
            display: 'grid',
            width: '100%',
            height: '100%',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridTemplateRows: `repeat(${rows}, 1fr)`,
            gap: '6px',
          }}
        >
          {allTeams.map(team => {
            const isSelected = selectedTeamId === team.id;
            return (
              <button
                key={team.id}
                onClick={() => { onSelect(team); onClose(); }}
                className="relative flex flex-col items-center justify-center rounded-2xl transition-all duration-200 active:scale-95 group overflow-hidden"
                style={{
                  background: isSelected
                    ? 'linear-gradient(135deg, rgba(6,182,212,0.20), rgba(139,92,246,0.14))'
                    : 'rgba(255,255,255,0.04)',
                  border: isSelected
                    ? '1.5px solid rgba(6,182,212,0.65)'
                    : '1.5px solid rgba(255,255,255,0.07)',
                  boxShadow: isSelected
                    ? '0 0 28px rgba(6,182,212,0.25), inset 0 1px 0 rgba(255,255,255,0.08)'
                    : 'none',
                }}
              >
                {/* Badge — scales to ~55% of cell height */}
                <div
                  className="transition-transform duration-200 group-hover:scale-110 flex items-center justify-center"
                  style={{
                    height: '55%',
                    filter: isSelected ? 'drop-shadow(0 0 10px rgba(6,182,212,0.55))' : 'none',
                  }}
                >
                  <img
                    src={badgeUrl(team.id)}
                    alt={team.name}
                    className="h-full w-full object-contain"
                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                  />
                  {/* Fallback initial */}
                  <div className="hidden w-8 h-8 rounded-full bg-white/10 items-center justify-center text-white/60 font-bold text-xs">
                    {(team.name || '?')[0]}
                  </div>
                </div>
                {/* Name — scales to remaining space */}
                <span
                  className="px-1 text-center font-semibold leading-tight line-clamp-2 transition-colors duration-200"
                  style={{
                    fontSize: 'clamp(8px, 1vw, 11px)',
                    color: isSelected ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.55)',
                    maxWidth: '90%',
                  }}
                >
                  {team.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Home panel ────────────────────────────────────────────────────────────────

function HomePanel({ matches, liveMatches, stories, onOpenMatch, onOpenStory, statsData, displayTitle, groups }) {
  const [pickerOpen, setPickerOpen]       = useState(false);
  const [filteredTeam, setFilteredTeam]   = useState(null); // { id, name } | null

  const all = useMemo(() => {
    const live = liveMatches.length > 0 ? liveMatches : [];
    const liveIds = new Set(live.map(m => m.pa_match_id));
    const merged = [...live, ...(matches || []).filter(m => !liveIds.has(m.pa_match_id))];
    if (!filteredTeam) return merged;
    const tid = String(filteredTeam.id);
    return merged.filter(m =>
      String(m.home_team_id) === tid || String(m.away_team_id) === tid
    );
  }, [matches, liveMatches, filteredTeam]);

  const newsItems = stories?.items || [];
  const showStats = statsData && statsData.games_played > 0;

  return (
    <>
      {/* Team picker fullscreen modal */}
      <TeamPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={setFilteredTeam}
        groups={groups}
        matches={matches}
        selectedTeamId={filteredTeam?.id}
      />

      <div className="h-full overflow-y-auto px-6 py-4">
        <div className="mb-6">
          {/* Clickable section head — opens team picker */}
          <button
            className="flex items-center gap-2 mb-4 group"
            onClick={() => setPickerOpen(true)}
          >
            <span className="w-1 h-5 rounded-full bg-primary inline-block flex-shrink-0" />
            <h2 className="text-foreground font-bold text-base group-hover:text-primary transition-colors">
              Fixtures & Results
            </h2>
            <span className="text-[10px] text-muted-foreground/60 font-medium group-hover:text-primary/70 transition-colors ml-0.5">
              ▾
            </span>
          </button>

          {/* Active team filter chip */}
          {filteredTeam && (
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-primary/30 bg-primary/10">
                <TeamBadge teamId={filteredTeam.id} teamName={filteredTeam.name} size={18} />
                <span className="text-xs font-semibold text-primary/90">{filteredTeam.name}</span>
                <button
                  onClick={() => setFilteredTeam(null)}
                  className="text-primary/50 hover:text-primary ml-0.5 text-xs leading-none transition-colors"
                  aria-label="Clear team filter"
                >
                  ✕
                </button>
              </div>
              <span className="text-xs text-muted-foreground">
                {all.length} match{all.length !== 1 ? 'es' : ''}
              </span>
            </div>
          )}

          <FixturesCarousel matches={all} onOpenMatch={onOpenMatch} />
        </div>

        {newsItems.length > 0 && (
          <div>
            <SectionHead label="Latest News" />
            <NewsGrid
              items={newsItems.slice(0, 4)}
              onOpen={onOpenStory}
              statsSlot={
                showStats
                  ? <TournamentStatsSidebar stats={statsData} title={`${displayTitle} · Stats`} />
                  : <CountdownPanel title={displayTitle} />
              }
            />
          </div>
        )}
      </div>
    </>
  );
}

function ResultsPanel({ matches, onOpenMatch }) {
  const results = useMemo(() =>
    (matches || []).filter(m =>
      m.status === 'finished' || m.status === 'ft' ||
      (m.home_score != null && m.away_score != null &&
       m.status !== 'scheduled' && m.status !== 'live')
    ), [matches]);

  if (!results.length) return <EmptyState icon="⚽" title="No results yet" sub="Results will appear once matches are played" />;

  return (
    <div className="h-full overflow-y-auto px-6 py-6 space-y-4">
      <SectionHead label="Results" />
      <FixturesCarousel matches={results} onOpenMatch={onOpenMatch} />
    </div>
  );
}

function UpcomingPanel({ matches, onOpenMatch }) {
  const upcoming = useMemo(() =>
    (matches || []).filter(m => !m.status || m.status === 'scheduled')
  , [matches]);

  if (!upcoming.length) return (
    <EmptyState icon="📅" title="No upcoming fixtures"
      sub={Date.now() < WC_OPENS.getTime() ? 'Fixtures will appear closer to the tournament' : 'All matches have been played'} />
  );

  return (
    <div className="h-full overflow-y-auto px-6 py-6 space-y-4">
      <SectionHead label="Upcoming Fixtures" />
      <FixturesCarousel matches={upcoming} onOpenMatch={onOpenMatch} />
    </div>
  );
}

function GroupsPanel({ groups }) {
  const list = groups?.groups || [];
  if (!list.length) return <EmptyState icon="📊" title="Group Standings" sub="Tables will appear once the tournament starts" />;
  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        {list.map(g => <GroupTable key={g.name} group={g} />)}
      </div>
    </div>
  );
}

// ── Pre-tournament: bookie top scorer predictions ────────────────────────────

function PreTournamentScorers() {
  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <SectionHead label="Top Goalscorers" />
      <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
        <span className="text-5xl">⚽</span>
        <p className="font-bold text-foreground">Top Goalscorers</p>
        <p className="text-muted-foreground text-sm max-w-xs">
          Stats will appear once matches are played
        </p>
      </div>
    </div>
  );
}

// ── Knockout bracket panel ────────────────────────────────────────────────────

// Elbow connector paths between a "many" column (manyN boxes) and the "few"
// column (manyN/2 boxes) it feeds. `toward` is which side the few column is on.
function _connectorPaths(manyN, toward, W, H) {
  const fewN = manyN / 2;
  const cy = i => H * (2 * i + 1) / (2 * manyN);
  const fy = j => H * (2 * j + 1) / (2 * fewN);
  const paths = [];
  for (let j = 0; j < fewN; j++) {
    const yTop = cy(2 * j), yBot = cy(2 * j + 1), yMid = fy(j);
    if (toward === 'right') {
      paths.push(`M0 ${yTop} H${W / 2} M0 ${yBot} H${W / 2} M${W / 2} ${yTop} V${yBot} M${W / 2} ${yMid} H${W}`);
    } else {
      paths.push(`M${W} ${yTop} H${W / 2} M${W} ${yBot} H${W / 2} M${W / 2} ${yTop} V${yBot} M${W / 2} ${yMid} H0`);
    }
  }
  return paths.join(' ');
}

function BracketPanel({ rounds }) {
  const [openMatch, setOpenMatch] = useState(null);
  if (!rounds?.length) {
    return <EmptyState icon="🏆" title="Knockout Bracket" sub="The bracket appears once the group stage is complete." />;
  }
  const isPlaceholder = (n) => /^(winner|loser|runner|group)\b/i.test((n || '').trim());
  const fmtDT = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) + ' ' +
        d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };
  const Badge = ({ name, teamId }) => isPlaceholder(name)
    ? <span className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center text-[9px] text-white/40 shrink-0">?</span>
    : <img src={badgeUrl(teamId)} alt="" className="w-5 h-5 object-contain shrink-0" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />;

  // One line: home badge · ABBR · score–score · ABBR · away badge; date/time below
  const Match = ({ m }) => {
    const fin = m.status === 'finished', live = m.status === 'live';
    const dec = fin && m.home_score != null && m.away_score != null;
    const hw = dec && m.home_score > m.away_score, aw = dec && m.away_score > m.home_score;
    const hPh = isPlaceholder(m.home_team_name), aPh = isPlaceholder(m.away_team_name);
    const played = m.home_score != null && m.away_score != null;
    const mid = played ? `${m.home_score}–${m.away_score}` : 'v';
    return (
      <div onClick={played ? () => setOpenMatch(m.pa_match_id) : undefined}
        className={`bg-[#0f1d35] border rounded-lg px-2 py-1 ${live ? 'border-red-400/60' : 'border-[#1e3a5f]/60'} ${played ? 'cursor-pointer hover:border-[#00D4AA]/60' : ''}`}>
        <div className="flex items-center justify-center gap-1.5 text-sm leading-tight">
          <Badge name={m.home_team_name} teamId={m.home_team_id} />
          <span className={hPh ? 'text-white/40' : hw ? 'text-white font-bold' : 'text-white/85'}>{teamAbbr(m.home_team_name)}</span>
          <span className="text-white/60 font-semibold tabular-nums px-0.5">{mid}</span>
          <span className={aPh ? 'text-white/40' : aw ? 'text-white font-bold' : 'text-white/85'}>{teamAbbr(m.away_team_name)}</span>
          <Badge name={m.away_team_name} teamId={m.away_team_id} />
        </div>
        <div className="text-center text-[10px] text-white/40 leading-tight mt-0.5">
          {live ? <span className="text-red-400 font-semibold">● {m.phase_label || 'LIVE'}</span> : fmtDT(m.kickoff_time)}
        </div>
      </div>
    );
  };

  const byRank = (rank) => rounds.find(r => r.rank === rank) || { matches: [] };
  const side = (rank, s) => byRank(rank).matches.filter(m => m.side === s);
  const SLOT = 64;
  const colHeight = 8 * SLOT;
  const CW = 18; // connector width

  const Column = ({ name, matches }) => (
    <div className="flex flex-col flex-1 min-w-0">
      <h3 className="text-xs font-bold uppercase tracking-wider text-[#00D4AA] mb-2 whitespace-nowrap h-4 text-center">{name}</h3>
      <div className="flex flex-col justify-around" style={{ height: colHeight }}>
        {matches.map(m => <Match key={m.pa_match_id} m={m} />)}
      </div>
    </div>
  );
  const Conn = ({ manyN, toward }) => (
    <div className="flex flex-col flex-shrink-0" style={{ width: CW }}>
      <div className="h-4 mb-2" />
      <svg width={CW} height={colHeight} style={{ overflow: 'visible' }}>
        <path d={_connectorPaths(manyN, toward, CW, colHeight)} stroke="#2a4a72" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );
  const StubToCentre = ({ toward }) => (
    <div className="flex flex-col flex-shrink-0" style={{ width: CW }}>
      <div className="h-4 mb-2" />
      <svg width={CW} height={colHeight} style={{ overflow: 'visible' }}>
        <path d={`M${toward === 'right' ? 0 : CW} ${colHeight / 2} H${toward === 'right' ? CW : 0}`} stroke="#2a4a72" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );

  return (
    <div className="w-full h-full overflow-x-auto">
      <div className="flex items-start" style={{ minWidth: 'min(100%, 1100px)' }}>
        <Column name="R32" matches={side(1, 'left')} />
        <Conn manyN={8} toward="right" />
        <Column name="R16" matches={side(2, 'left')} />
        <Conn manyN={4} toward="right" />
        <Column name="QF" matches={side(3, 'left')} />
        <Conn manyN={2} toward="right" />
        <Column name="SF" matches={side(4, 'left')} />
        <StubToCentre toward="right" />
        <div className="flex flex-col flex-shrink-0 justify-center px-1" style={{ width: 160, height: colHeight + 16 }}>
          {byRank(6).matches.map(m => (
            <div key={m.pa_match_id} className="mb-4">
              <p className="text-center text-sm font-bold text-white mb-1">FINAL</p>
              <Match m={m} />
            </div>
          ))}
          {byRank(5).matches.map(m => (
            <div key={m.pa_match_id}>
              <p className="text-center text-[10px] text-white/50 font-semibold mb-1">3rd place</p>
              <Match m={m} />
            </div>
          ))}
        </div>
        <StubToCentre toward="left" />
        <Column name="SF" matches={side(4, 'right')} />
        <Conn manyN={2} toward="left" />
        <Column name="QF" matches={side(3, 'right')} />
        <Conn manyN={4} toward="left" />
        <Column name="R16" matches={side(2, 'right')} />
        <Conn manyN={8} toward="left" />
        <Column name="R32" matches={side(1, 'right')} />
      </div>
      {openMatch && <BracketMatchModal matchId={openMatch} onClose={() => setOpenMatch(null)} />}
    </div>
  );
}

// ── Main scorers panel ────────────────────────────────────────────────────────

// Scorer card photo: club photo for club comps, WC photo for the World Cup,
// each falling back to the other when the player has no photo of that kind.
function ScorerPhoto({ playerId, preferWc }) {
  const { src } = usePlayerPhoto(playerId, preferWc);
  if (!src) return null;
  return (
    <div className="absolute inset-0" style={{
      backgroundImage: `url(${src})`,
      backgroundSize: 'auto 100%',
      backgroundPosition: 'center top',
      backgroundRepeat: 'no-repeat',
    }} />
  );
}

function ScorersPanel({ scorers, matches, groups, onOpenPlayer, isWc = true }) {
  // Build team_id → team_name AND team_name → team_id maps from all WC data sources
  const { teamNames, teamNameToId } = useMemo(() => {
    const teamNames = {}, teamNameToId = {};
    const add = (id, name) => {
      if (!id || !name) return;
      const s = String(id).trim();
      teamNames[s] = name;
      if (s.startsWith('t')) teamNames[s.slice(1)] = name;
      else teamNames[`t${s}`] = name;
      teamNameToId[name.toLowerCase()] = s;
    };
    (matches || []).forEach(m => {
      add(m.home_team_id, m.home_team_name);
      add(m.away_team_id, m.away_team_name);
    });
    (groups?.groups || []).forEach(g =>
      (g.rows || []).forEach(r => add(r.team_id, r.team))
    );
    return { teamNames, teamNameToId };
  }, [matches, groups]);

  // Map a nationality string (e.g. "Mexican", "South African", "England") to a WC team name/id
  const resolveNationality = (nat) => {
    if (!nat) return null;
    const n = nat.toLowerCase().trim();
    // Try direct match (e.g. "Mexico", "South Africa")
    if (teamNameToId[n]) return { name: nat, team_id: teamNameToId[n] };
    // Try common adjective → country forms against every known team name
    for (const [tname, tid] of Object.entries(teamNameToId)) {
      if (n.startsWith(tname) || tname.startsWith(n)) return { name: teamNames[tid] || tname, team_id: tid };
    }
    return null;
  };

  // Enrich: use nationality → WC team lookup; never fall back to club profile
  const enriched = useMemo(() =>
    scorers.map(s => {
      let team    = s.team || teamNames[String(s.team_id).trim()] || '';
      let team_id = s.team_id || '';
      // If still missing, resolve from nationality field
      if (!team || !team_id) {
        const resolved = resolveNationality(s.nationality);
        if (resolved) { team = resolved.name; team_id = resolved.team_id; }
      }
      return { ...s, team, team_id };
    }), [scorers, teamNames, teamNameToId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Before any goals are scored, show bookie golden boot predictions instead
  if (!enriched.length) return <PreTournamentScorers />;

  // Compute proper tied ranks (players with equal goals share a rank)
  const ranked = enriched.slice(0, 15).map((s) => ({
    ...s,
    displayRank: enriched.filter(x => x.goals > s.goals).length + 1,
  }));

  const rankColor = (r) =>
    r === 1 ? 'text-yellow-400' : r === 2 ? 'text-slate-300' : r === 3 ? 'text-amber-600' : 'text-muted-foreground';

  const count = ranked.length;

  // ── Hero layout: 1–2 scorers ─────────────────────────────────────────────
  if (count <= 2) {
    return (
      <div className="h-full flex flex-col px-6 py-6">
        <SectionHead label="Top Goalscorers" />
        <div className={`flex-1 flex gap-5 mt-4 ${count === 1 ? 'justify-center' : ''}`}>
          {ranked.map((s) => {
            return (
              <button key={s.player_id || s.player}
                onClick={() => s.player_id && onOpenPlayer?.({ player_id: s.player_id, player: s.player, team_id: s.team_id })}
                className={`flex flex-col bg-card border border-border rounded-2xl overflow-hidden
                            text-left hover:border-primary/50 transition-all duration-200 cursor-pointer
                            ${count === 1 ? 'w-72' : 'flex-1'}`}>
                {/* Player image — fills top portion */}
                <div className="relative flex-1 min-h-0 overflow-hidden bg-muted">
                  <ScorerPhoto playerId={s.player_id} preferWc={isWc} />
                  {/* Gradient overlay so rank/goals are readable */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  {/* Rank badge top-left */}
                  <span className={`absolute top-4 left-4 text-4xl font-black leading-none ${rankColor(s.displayRank)}`}>
                    #{s.displayRank}
                  </span>
                  {/* Goals count bottom-right */}
                  <div className="absolute bottom-4 right-4 text-right">
                    <p className="text-white font-black text-6xl tabular-nums leading-none">{s.goals}</p>
                    <p className="text-white/60 text-xs uppercase tracking-widest mt-0.5">goals</p>
                  </div>
                </div>
                {/* Player info strip */}
                <div className="px-4 py-3 flex-shrink-0 border-t border-border/50">
                  <p className="text-foreground font-bold text-base truncate">{s.player}</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {s.team_id && <TeamBadge teamId={s.team_id} teamName={s.team} size={18} />}
                    {s.team && <p className="text-muted-foreground text-sm truncate">{s.team}</p>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Grid layout: 3–9 scorers — fills full height ────────────────────────
  if (count <= 9) {
    const cols = count === 4 ? 2 : 3;
    const rows = Math.ceil(count / cols);
    return (
      <div className="h-full flex flex-col px-6 py-6">
        <SectionHead label="Top Goalscorers" />
        <div className="flex-1 min-h-0 mt-4" style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: '12px',
        }}>
          {ranked.map((s) => {
            return (
              <button key={s.player_id || s.player}
                onClick={() => s.player_id && onOpenPlayer?.({ player_id: s.player_id, player: s.player, team_id: s.team_id })}
                className="flex flex-col bg-card border border-border rounded-xl overflow-hidden
                           text-left hover:border-primary/50 transition-colors cursor-pointer w-full h-full">
                {/* Player image — fills card top */}
                <div className="relative flex-1 min-h-0 bg-muted overflow-hidden">
                  <ScorerPhoto playerId={s.player_id} preferWc={isWc} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-transparent" />
                  <span className={`absolute top-3 left-3 text-2xl font-black leading-none ${rankColor(s.displayRank)}`}>
                    #{s.displayRank}
                  </span>
                  <div className="absolute bottom-3 right-3 text-right">
                    <p className="text-white font-black text-4xl tabular-nums leading-none">{s.goals}</p>
                    <p className="text-white/60 text-[10px] uppercase tracking-widest">goals</p>
                  </div>
                </div>
                {/* Player info strip */}
                <div className="px-3 py-2 flex-shrink-0 border-t border-border/50">
                  <p className="text-foreground font-semibold text-sm truncate">{s.player}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {s.team_id && <TeamBadge teamId={s.team_id} teamName={s.team} size={14} />}
                    {s.team && <p className="text-muted-foreground text-xs truncate">{s.team}</p>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Compact layout: 10+ scorers ──────────────────────────────────────────
  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <SectionHead label="Top Goalscorers" />
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 mt-4">
        {ranked.map((s) => (
          <button key={s.player_id || s.player}
            onClick={() => s.player_id && onOpenPlayer?.({ player_id: s.player_id, player: s.player, team_id: s.team_id })}
            className="flex items-center gap-4 bg-card border border-border rounded-xl p-4
                       text-left hover:border-primary/50 hover:bg-card/80 transition-colors
                       cursor-pointer w-full">
            <span className={`text-xl font-black w-7 text-center flex-shrink-0 tabular-nums ${rankColor(s.displayRank)}`}>
              {s.displayRank}
            </span>
            <PlayerPhoto playerId={s.player_id} playerName={s.player} size={52} />
            <div className="flex-1 min-w-0">
              <p className="text-foreground font-semibold text-sm truncate">{s.player}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                {s.team_id && <TeamBadge teamId={s.team_id} teamName={s.team} size={16} />}
                {s.team && <p className="text-muted-foreground text-xs truncate">{s.team}</p>}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-foreground font-black text-2xl tabular-nums">{s.goals}</p>
              <p className="text-muted-foreground text-[10px] uppercase tracking-wide">goals</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function StoriesPanel({ stories, onOpen }) {
  const items = stories?.items || [];
  if (!items.length) return <EmptyState icon="📰" title="Latest News" sub="Loading stories…" />;

  return (
    <div className="h-full overflow-y-auto px-6 py-6 space-y-8">
      <div>
        <SectionHead label="Latest News" />
        <NewsGrid items={items.slice(0, 4)} onOpen={onOpen} />
      </div>
      {items.length > 4 && (
        <div>
          <SectionHead label="More Stories" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {items.slice(4, 12).map((item) => {
              const img = itemImage(item, '16x9');
              const id  = extractArticleId(item);
              return (
                <button key={id || item.headline} onClick={() => id && onOpen(item)}
                  className="text-left group rounded-xl overflow-hidden border bg-card
                             hover:border-primary/50 transition-colors">
                  {img ? (
                    <div className="aspect-video overflow-hidden">
                      <img src={img} alt={item.headline}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                    </div>
                  ) : (
                    <div className="aspect-video bg-muted flex items-center justify-center">
                      <span className="text-3xl opacity-30">📰</span>
                    </div>
                  )}
                  <div className="p-3">
                    <p className="font-semibold text-sm leading-snug text-foreground
                                  group-hover:text-primary transition-colors line-clamp-2">
                      {item.headline}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{fmtIssued(item.issued)}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function GroupTable({ group }) {
  const rows = group.rows || [];
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <span className="w-1 h-4 rounded-full bg-primary flex-shrink-0" />
        <h3 className="text-foreground font-bold text-sm">{group.name || 'Table'}</h3>
      </div>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-muted-foreground border-b border-border/50">
            <th className="text-left px-4 py-2 font-semibold" colSpan={2}>Team</th>
            {['P','W','D','L','GD','Pts'].map(h => (
              <th key={h} className="text-center py-2 px-1 font-semibold w-7">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((t, i) => (
            <tr key={t.team_id || i}
              className={`border-b border-border/30 last:border-0 ${i < 2 ? 'bg-primary/5' : ''}`}>
              <td className="pl-4 py-2.5 w-6 text-muted-foreground">{t.position ?? i + 1}</td>
              <td className="py-2.5 pr-2">
                <div className="flex items-center gap-2">
                  <TeamIntelTooltip teamId={t.team_id} teamName={t.team} size={20}>
                    <TeamBadge teamId={t.team_id} teamName={t.team} size={20} />
                  </TeamIntelTooltip>
                  <span className="text-foreground font-medium truncate max-w-[100px]">{t.team}</span>
                </div>
              </td>
              {[t.played, t.won, t.drawn, t.lost].map((v, j) => (
                <td key={j} className="text-center py-2.5 px-1 text-muted-foreground tabular-nums">{v}</td>
              ))}
              <td className="text-center py-2.5 px-1 text-muted-foreground tabular-nums">
                {t.gd > 0 ? `+${t.gd}` : t.gd}
              </td>
              <td className="text-center py-2.5 px-1 text-foreground font-bold tabular-nums pr-3">{t.points}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── story reader overlay ──────────────────────────────────────────────────────

function StoryReader({ story, onClose }) {
  const [article, setArticle] = useState(null);
  const id = extractArticleId(story);

  useEffect(() => {
    if (!id) return;
    setArticle(null);
    axios.get(`${API_URL}/api/football/hub/article`,
      { params: { item_id: id }, headers: authH() }
    ).then(r => setArticle(r.data)).catch(() => setArticle({}));
  }, [id]);

  const bodyHtml = article?.body_html || (article?.body_text ? `<p>${article.body_text}</p>` : null);

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-8"
      onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl overflow-hidden
                      max-w-3xl w-full max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Sticky headline — always visible, no image above it */}
        <div className="px-8 py-5 border-b border-border flex-shrink-0 bg-card">
          <span className="inline-block bg-red-500 text-white text-xs font-bold
                           px-2 py-0.5 rounded mb-2 uppercase">Football</span>
          <h2 className="text-foreground font-black text-2xl leading-tight">
            {article?.headline || story.headline}
          </h2>
          {(article?.issued || story.issued) && (
            <p className="text-muted-foreground text-xs mt-1">{fmtIssued(article?.issued || story.issued)}</p>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-8">
          {article === null ? (
            <p className="text-muted-foreground">Loading article…</p>
          ) : bodyHtml ? (
            <ArticleBody html={bodyHtml} />
          ) : (
            <p className="text-muted-foreground">Article content not available.</p>
          )}
        </div>
        <div className="px-8 py-5 border-t border-border flex-shrink-0">
          <button onClick={onClose}
            className="w-full bg-secondary hover:bg-secondary/80 text-foreground font-bold
                       py-4 rounded-xl text-base transition-colors border border-border">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── live clock ────────────────────────────────────────────────────────────────

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="text-right flex-shrink-0">
      <p className="text-foreground font-mono font-bold text-base tabular-nums leading-tight">
        {now.toLocaleTimeString('en-GB', { hour:'2-digit', minute:'2-digit', second:'2-digit' })}
      </p>
      <p className="text-muted-foreground text-[11px] leading-tight">
        {now.toLocaleDateString('en-GB', { weekday:'short', day:'numeric', month:'short' })}
      </p>
    </div>
  );
}

// ── TOURNAMENT STATS helpers ──────────────────────────────────────────────────

function buildStatTiles(stats) {
  if (!stats || stats.games_played === 0) return [];
  return [
    {
      value: stats.total_goals,
      label: 'Goals Scored',
      sub: `${stats.games_played} matches played`,
    },
    {
      value: stats.avg_goals?.toFixed(2),
      label: 'Goals / Game',
      sub: 'tournament average',
    },
    {
      value: stats.clean_sheets,
      label: 'Clean Sheets',
      sub: `${stats.games_played ? Math.round(stats.clean_sheets / stats.games_played * 100) : 0}% of matches`,
    },
    stats.biggest_win ? {
      value: `${stats.biggest_win.home_score}–${stats.biggest_win.away_score}`,
      label: 'Biggest Win',
      sub: `${stats.biggest_win.home} v ${stats.biggest_win.away}`,
    } : null,
    stats.top_scoring_group ? {
      value: stats.top_scoring_group_goals,
      // Backend picks the highest-scoring round/phase — a group during the group
      // stage, a knockout round after — so "Stage" reads correctly throughout.
      label: 'Top Scoring Stage',
      sub: stats.top_scoring_group,
    } : null,
    {
      value: `${stats.games_played}/${stats.total_matches || '?'}`,
      label: 'Matches Played',
      sub: `${stats.total_matches ? Math.round(stats.games_played / stats.total_matches * 100) : 0}% complete`,
    },
  ].filter(Boolean);
}

function StatTile({ tile }) {
  return (
    <div className="rounded-xl border border-border/60 bg-card flex flex-col items-center justify-center py-4 px-2 text-center">
      <span className="text-2xl font-black tabular-nums leading-none mb-1" style={{ color: '#ff1654' }}>
        {tile.value}
      </span>
      <span className="text-xs font-bold text-foreground leading-tight">{tile.label}</span>
      {tile.sub && (
        <span className="text-[10px] text-muted-foreground mt-0.5 leading-tight line-clamp-2">{tile.sub}</span>
      )}
    </div>
  );
}

// Side-by-side panel: 3 columns × 2 rows of stat tiles
function TournamentStatsSidebar({ stats, title }) {
  const tiles = buildStatTiles(stats);
  if (!tiles.length) return null;
  return (
    <div className="h-full flex flex-col">
      <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-3">
        {title || 'Tournament Stats'}
      </p>
      <div className="grid grid-cols-3 gap-3 flex-1">
        {tiles.slice(0, 6).map((tile, i) => <StatTile key={i} tile={tile} />)}
      </div>
    </div>
  );
}

// ── Squads feature ────────────────────────────────────────────────────────────

// ── Season heatmap canvas (same renderer as MatchDetailPage) ─────────────────
function HeatmapPitch({ points }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const W = canvas.width, H = canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, W, H);
    const stripes = 8, sw = W / stripes;
    for (let i = 0; i < stripes; i++) {
      ctx.fillStyle = i % 2 === 0 ? '#166534' : '#15803d';
      ctx.fillRect(i * sw, 0, sw, H);
    }
    const PL=14, PR=14, PT=10, PB=22, PW=W-PL-PR, PH=H-PT-PB;
    const marks = () => {
      ctx.strokeStyle='rgba(255,255,255,0.35)'; ctx.lineWidth=1.5;
      ctx.strokeRect(PL,PT,PW,PH);
      ctx.beginPath(); ctx.moveTo(W/2,PT); ctx.lineTo(W/2,PT+PH); ctx.stroke();
      ctx.beginPath(); ctx.arc(W/2,PT+PH/2,PH*0.19,0,Math.PI*2); ctx.stroke();
      ctx.fillStyle='rgba(255,255,255,0.4)'; ctx.beginPath(); ctx.arc(W/2,PT+PH/2,3,0,Math.PI*2); ctx.fill();
      ctx.lineWidth=1; ctx.strokeStyle='rgba(255,255,255,0.28)';
      const pbW=PW*0.14,pbH=PH*0.58,pbY=PT+PH*0.21;
      ctx.strokeRect(PL,pbY,pbW,pbH); ctx.strokeRect(PL+PW-pbW,pbY,pbW,pbH);
      ctx.strokeStyle='rgba(255,255,255,0.18)';
      const syW=PW*0.055,syH=PH*0.28,syY=PT+PH*0.36;
      ctx.strokeRect(PL,syY,syW,syH); ctx.strokeRect(PL+PW-syW,syY,syW,syH);
      ctx.fillStyle='rgba(255,255,255,0.45)'; ctx.font='600 10px sans-serif';
      ctx.fillText('DEF',PL+4,H-4); ctx.fillText('ATT',PL+PW-28,H-4);
    };
    if (!points?.length) { marks(); return; }
    const dc=document.createElement('canvas'); dc.width=W; dc.height=H;
    const dCtx=dc.getContext('2d');
    dCtx.save(); dCtx.beginPath(); dCtx.rect(PL,PT,PW,PH); dCtx.clip();
    const radius=Math.min(PW,PH)*0.12;
    points.forEach(p => {
      const x=PL+p.x/100*PW, y=PT+p.y/100*PH;
      const g=dCtx.createRadialGradient(x,y,0,x,y,radius);
      g.addColorStop(0,'rgba(255,60,0,0.22)'); g.addColorStop(1,'rgba(255,60,0,0)');
      dCtx.fillStyle=g; dCtx.beginPath(); dCtx.arc(x,y,radius,0,Math.PI*2); dCtx.fill();
    });
    dCtx.restore();
    ctx.drawImage(dc,0,0); marks();
  }, [points]);
  return <canvas ref={canvasRef} width={320} height={200} className="w-full rounded-lg" />;
}

// ── Admin: WC StatsAPI competition/season config panel ───────────────────────
function WcStatsApiConfigPanel() {
  const [cfg,      setCfg]      = useState(null);
  const [compId,   setCompId]   = useState('');
  const [seasonId, setSeasonId] = useState('');
  const [seasons,  setSeasons]  = useState(null);
  const [saving,   setSaving]   = useState(false);
  const [msg,      setMsg]      = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/admin/football/worldcup/statsapi-config`, { headers: authH() })
      .then(r => { setCfg(r.data); setCompId(r.data.comp_id || ''); setSeasonId(r.data.season_id || ''); })
      .catch(() => {});
  }, []);

  const listSeasons = async () => {
    if (!compId.trim()) return;
    setSeasons(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/football/worldcup/statsapi-seasons`,
        { params: { comp_id: compId }, headers: authH() });
      setSeasons(r.data.seasons || []);
    } catch { setSeasons([]); }
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await axios.post(`${API_URL}/api/admin/football/worldcup/statsapi-config`,
        { comp_id: compId, season_id: seasonId }, { headers: authH() });
      setMsg('Saved ✓');
    } catch { setMsg('Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 pt-3 space-y-2" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-white/25 text-[10px] font-bold uppercase tracking-widest">WC Heatmap Competition</p>
      <p className="text-white/20 text-[10px]">
        Set the StatsAPI comp &amp; season IDs to get WC-specific heatmaps.<br/>
        Current: {cfg?.comp_id ? <span className="text-cyan-400/60 font-mono">{cfg.comp_id} / {cfg.season_id}</span> : <span>not set</span>}
      </p>
      <div className="flex gap-2">
        <input value={compId} onChange={e => setCompId(e.target.value)}
          placeholder="comp_XXXX" className="flex-1 rounded px-2 py-1 text-[10px] text-white font-mono bg-white/5 border border-white/10 outline-none focus:border-cyan-500/40" />
        <button onClick={listSeasons} className="px-2 py-1 rounded text-[10px] text-white/50 hover:text-white bg-white/5 border border-white/10">
          List seasons
        </button>
      </div>
      {seasons !== null && (
        <div className="rounded overflow-hidden text-[10px]" style={{ border: '1px solid rgba(255,255,255,0.08)', maxHeight: 120, overflowY: 'auto' }}>
          {seasons.length === 0 ? (
            <p className="p-2 text-white/30">No seasons found</p>
          ) : seasons.map(s => (
            <button key={s.id || s.season_id} onClick={() => setSeasonId(s.id || s.season_id)}
              className="w-full text-left px-2 py-1.5 hover:bg-white/5 transition-colors border-b last:border-0"
              style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
              <span className="text-white/70">{s.name || s.label || s.id}</span>
              <span className="text-cyan-400/50 font-mono ml-2">{s.id || s.season_id}</span>
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-center">
        <input value={seasonId} onChange={e => setSeasonId(e.target.value)}
          placeholder="sn_XXXXXX (season ID)" className="flex-1 rounded px-2 py-1 text-[10px] text-white font-mono bg-white/5 border border-white/10 outline-none focus:border-cyan-500/40" />
        <button onClick={save} disabled={saving}
          className="px-2 py-1 rounded text-[10px] font-semibold text-cyan-400 bg-cyan-900/30 border border-cyan-500/30"
          style={{ opacity: saving ? 0.5 : 1 }}>
          {saving ? '…' : 'Save'}
        </button>
        {msg && <span className="text-[10px] text-green-400">{msg}</span>}
      </div>
    </div>
  );
}

// ── WC Player detail modal ────────────────────────────────────────────────────
function WcPlayerModal({ player, teamId, onClose }) {
  const { user }          = useAuth();
  const isAdmin           = user?.is_admin;
  const [detail, setDetail]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [tournamentStats, setTournamentStats] = useState(null);
  const [loadingTs, setLoadingTs]     = useState(false);
  const [showMap, setShowMap]         = useState(false);    // admin mapping panel
  const [mapSearch, setMapSearch]     = useState('');
  const [mapResults, setMapResults]   = useState(null);
  const [mapSearching, setMapSearching] = useState(false);
  const [mapSaving, setMapSaving]     = useState(false);

  const loadDetail = () => {
    if (!player?.player_id) return;
    setLoading(true); setDetail(null);
    axios
      .get(`${API_URL}/api/football/worldcup/player/${player.player_id}/detail`, { headers: authH() })
      .then(r => { setDetail(r.data); setMapSearch(r.data.full_name || player.name || ''); })
      .catch(() => setDetail({ error: true }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadDetail(); }, [player?.player_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!player?.player_id) return;
    setTournamentStats(null);
    setLoadingTs(true);
    axios.get(`${API_URL}/api/football/worldcup/player/${player.player_id}/tournament-stats`, { headers: authH() })
      .then(r => setTournamentStats(r.data))
      .catch(() => setTournamentStats({ available: false }))
      .finally(() => setLoadingTs(false));
  }, [player?.player_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchStatsApi = async () => {
    if (!mapSearch.trim()) return;
    setMapSearching(true); setMapResults(null);
    try {
      const r = await axios.get(`${API_URL}/api/admin/football/worldcup/player-search-statsapi`,
        { params: { name: mapSearch }, headers: authH() });
      setMapResults(r.data.candidates || []);
    } catch { setMapResults([]); }
    finally { setMapSearching(false); }
  };

  const saveMapping = async (statsapiId) => {
    setMapSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/football/worldcup/player-mapping`,
        { pa_player_id: player.player_id, statsapi_player_id: statsapiId, full_name: detail?.full_name || player.name },
        { headers: authH() });
      setShowMap(false); setMapResults(null);
      loadDetail(); // reload with new mapping
    } catch { alert('Save failed'); }
    finally { setMapSaving(false); }
  };

  const src = player?.player_id ? wcPlayerUrl(player.player_id) : null;
  const [imgErr, setImgErr] = useState(false);

  const STAT_COLS = [
    { key: 'appearances', label: 'Apps' },
    { key: 'goals',       label: '⚽' },
    { key: 'assists',     label: '🎯' },
    { key: 'yellow_cards',label: '🟨' },
    { key: 'red_cards',   label: '🟥' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
         style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
         onClick={onClose}>
      <div
        className="relative w-full max-w-lg rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
        style={{ background: '#050b14', maxHeight: '92dvh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Hero — photo + name */}
        <div className="relative flex-shrink-0" style={{ height: 220 }}>
          {src && !imgErr ? (
            <img src={src} alt={player.name} className="absolute inset-0 w-full h-full object-cover"
                 style={{ objectPosition: 'center 10%' }} onError={() => setImgErr(true)} />
          ) : (
            <div className="absolute inset-0 bg-white/5 flex items-center justify-center">
              <span className="text-white/10 text-8xl">◉</span>
            </div>
          )}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #050b14 0%, rgba(5,11,20,0.5) 55%, transparent 100%)' }} />
          {/* Close */}
          <button onClick={onClose}
            className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white transition-colors"
            style={{ background: 'rgba(0,0,0,0.5)' }}>✕</button>
          {/* Shirt number */}
          <div className="absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center font-black text-white text-sm"
               style={{ background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.2)' }}>
            {player.shirt || '·'}
          </div>
          {/* Name overlay */}
          <div className="absolute bottom-0 inset-x-0 px-4 pb-3">
            <h2 className="text-white font-black text-xl leading-tight">{detail?.full_name || player.name}</h2>
            {detail && (
              <p className="text-white/50 text-xs mt-0.5">
                {detail.position && <span className="mr-2">{detail.position}</span>}
                {detail.nationality}
              </p>
            )}
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-4 pb-6 space-y-5 pt-4">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            </div>
          )}

          {!loading && detail && !detail.error && (
            <>
              {/* Bio chips */}
              {(detail.dob || detail.height || detail.weight) && (
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Date of Birth', val: detail.dob },
                    { label: 'Height',        val: detail.height },
                    { label: 'Weight',        val: detail.weight },
                  ].filter(c => c.val).map(c => (
                    <div key={c.label} className="rounded-xl p-2.5 text-center" style={{ background: 'rgba(255,255,255,0.05)' }}>
                      <p className="text-white/40 text-[10px] font-medium mb-0.5">{c.label}</p>
                      <p className="text-white font-bold text-sm">{c.val}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Stats — WC tournament stats take priority; fall back to club season stats */}
              {loadingTs && (
                <div className="flex items-center gap-2 py-2">
                  <div className="w-4 h-4 border border-white/20 border-t-white/50 rounded-full animate-spin flex-shrink-0" />
                  <p className="text-white/30 text-xs">Loading tournament stats…</p>
                </div>
              )}

              {!loadingTs && tournamentStats?.available && (() => {
                const ts = tournamentStats.stats;
                const rCol = ts.rating >= 8 ? '#10b981' : ts.rating >= 7 ? '#84cc16' : ts.rating >= 6 ? '#f59e0b' : '#ef4444';
                const fmtDuel = (won, pct) =>
                  won != null && pct != null ? `${won} (${Math.round(pct)}%)` : (won ?? null);
                const chips = [
                  { key: 'goals',      label: 'Goals',        val: ts.scoring?.goals,                                                hi: '#10b981' },
                  { key: 'assists',    label: 'Assists',       val: ts.scoring?.assists,                                             hi: '#38bdf8' },
                  { key: 'big_ch',     label: 'Big chances',  val: ts.scoring?.big_chances_created,                                 hi: '#f59e0b' },
                  { key: 'shots',      label: 'Shots',        val: ts.shooting?.total_shots,                                        hi: null },
                  { key: 'shots_ot',   label: 'On target',    val: ts.shooting?.shots_on_target,                                    hi: null },
                  { key: 'passes',     label: 'Passes',       val: ts.passing?.total_passes,                                        hi: null },
                  { key: 'pass_acc',   label: 'Pass acc',     val: ts.passing?.pass_accuracy != null ? `${Number(ts.passing.pass_accuracy).toFixed(0)}%` : null, hi: null },
                  { key: 'key_pass',   label: 'Key passes',   val: ts.passing?.key_passes,                                          hi: null },
                  { key: 'crosses',    label: 'Acc. crosses', val: ts.passing?.accurate_crosses,                                    hi: null },
                  { key: 'tackles',    label: 'Tackles',      val: ts.defending?.tackles,                                           hi: null },
                  { key: 'intercepts', label: 'Intercepts',   val: ts.defending?.interceptions,                                     hi: null },
                  { key: 'ground',     label: 'Ground duels', val: fmtDuel(ts.duels?.ground_duels_won, ts.duels?.ground_duels_won_percentage), hi: null },
                  { key: 'aerial',     label: 'Aerial duels', val: fmtDuel(ts.duels?.aerial_duels_won, ts.duels?.aerial_duels_won_percentage), hi: null },
                  { key: 'dribbles',   label: 'Dribbles',     val: ts.duels?.successful_dribbles,                                   hi: null },
                  { key: 'yellow',     label: 'Yellow',       val: ts.discipline?.yellow_cards,                                     hi: '#eab308' },
                  { key: 'red',        label: 'Red card',     val: ts.discipline?.red_cards,                                        hi: '#ef4444' },
                ].filter(c => c.val != null && c.val !== 0 && c.val !== '0%');
                return (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest">
                        🏆 World Cup 2026
                      </p>
                      <div className="flex items-center gap-2">
                        <span className="text-white/40 text-xs">
                          {ts.appearances} app{ts.appearances !== 1 ? 's' : ''} · {ts.minutes_played}′
                        </span>
                        {ts.rating > 0 && (
                          <span className="font-black text-sm px-2 py-0.5 rounded-lg text-white"
                            style={{ background: rCol }}>{ts.rating.toFixed(1)}</span>
                        )}
                      </div>
                    </div>
                    {chips.length > 0 ? (
                      <div className="grid grid-cols-3 gap-1.5">
                        {chips.map(c => (
                          <div key={c.key} className="rounded-xl p-2 text-center"
                            style={{ background: 'rgba(255,255,255,0.05)' }}>
                            <p className="text-white/40 text-[10px] font-medium mb-0.5 truncate">{c.label}</p>
                            <p className="font-bold text-base leading-tight"
                              style={{ color: c.hi || 'white' }}>{c.val}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-white/25 text-xs text-center py-2">No stats yet this tournament</p>
                    )}
                  </div>
                );
              })()}

              {/* Fall back to club season stats only when no WC stats available */}
              {!loadingTs && !tournamentStats?.available && detail.seasons?.length > 0 && (
                <div>
                  <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mb-2">Season Stats (All Comps)</p>
                  <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                    <table className="w-full text-xs">
                      <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                          <th className="text-left px-3 py-2 text-white/40 font-semibold">Season</th>
                          {STAT_COLS.map(c => (
                            <th key={c.key} className="px-2 py-2 text-white/40 font-semibold text-center">{c.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {detail.seasons.map((s, i) => (
                          <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                            <td className="px-3 py-2">
                              <p className="text-white font-semibold">{s.season}</p>
                              {s.competition && <p className="text-white/30 text-[10px] truncate">{s.competition}</p>}
                            </td>
                            {STAT_COLS.map(c => (
                              <td key={c.key} className="px-2 py-2 text-center">
                                <span className={`font-bold ${c.key === 'goals' ? 'text-green-400' : c.key === 'assists' ? 'text-sky-400' : 'text-white'}`}>
                                  {s[c.key] ?? '—'}
                                </span>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Heatmap — only shown when data is available, no placeholder */}
              {detail.heatmap?.available && (
                <div>
                  <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mb-2">Season Heatmap</p>
                  <HeatmapPitch points={detail.heatmap.points} />
                </div>
              )}
            </>
          )}

          {!loading && detail?.error && (
            <p className="text-white/30 text-sm text-center py-6">Could not load player data</p>
          )}

          {/* ── Admin: StatsAPI mapping panel ── */}
          {isAdmin && !loading && detail && !detail.error && (
            <div className="border-t pt-4" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
              {detail.statsapi_player_id ? (
                <div className="flex items-center justify-between">
                  <p className="text-white/25 text-[10px]">
                    StatsAPI ID: <span className="text-cyan-400/60 font-mono">{detail.statsapi_player_id}</span>
                    {' '}· stats from <span className="text-cyan-400/60">{detail.stats_source}</span>
                  </p>
                  <button onClick={() => setShowMap(v => !v)}
                    className="text-[10px] text-white/30 hover:text-white/60 transition-colors ml-2">
                    {showMap ? 'Cancel' : 'Re-link'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowMap(v => !v)}
                  className="w-full py-2 rounded-xl text-xs font-semibold text-cyan-400/70 hover:text-cyan-400 transition-colors"
                  style={{ background: 'rgba(6,182,212,0.07)', border: '1px solid rgba(6,182,212,0.2)' }}
                >
                  🔗 Link to StatsAPI — enables heatmap &amp; live stats
                </button>
              )}

              {showMap && (
                <div className="mt-3 space-y-2">
                  <div className="flex gap-2">
                    <input
                      value={mapSearch}
                      onChange={e => setMapSearch(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && searchStatsApi()}
                      placeholder="Search player name…"
                      className="flex-1 rounded-lg px-3 py-2 text-xs text-white bg-white/5 border border-white/10 outline-none focus:border-cyan-500/50"
                    />
                    <button onClick={searchStatsApi} disabled={mapSearching}
                      className="px-3 py-2 rounded-lg text-xs font-semibold text-white transition-colors"
                      style={{ background: 'rgba(6,182,212,0.2)', opacity: mapSearching ? 0.5 : 1 }}>
                      {mapSearching ? '…' : 'Search'}
                    </button>
                  </div>

                  {mapResults !== null && (
                    <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
                      {mapResults.length === 0 ? (
                        <p className="text-white/30 text-xs p-3 text-center">No results found</p>
                      ) : mapResults.map(c => (
                        <button key={c.id}
                          onClick={() => saveMapping(c.id)}
                          disabled={mapSaving}
                          className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-white/5 transition-colors border-b last:border-0"
                          style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                          <div>
                            <p className="text-white text-xs font-semibold">{c.name}</p>
                            <p className="text-white/40 text-[10px]">{c.position} · {c.nationality} · {c.team}</p>
                          </div>
                          <span className="text-cyan-400/60 font-mono text-[10px] ml-2">{c.id}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Manual ID entry */}
                  <div className="flex gap-2 items-center">
                    <p className="text-white/25 text-[10px] flex-shrink-0">Manual ID:</p>
                    <input
                      placeholder="e.g. pl_84027040"
                      className="flex-1 rounded-lg px-3 py-1.5 text-xs text-white font-mono bg-white/5 border border-white/10 outline-none focus:border-cyan-500/50"
                      onKeyDown={e => { if (e.key === 'Enter' && e.target.value.trim()) saveMapping(e.target.value.trim()); }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Individual player tile — uniform size, WC photo
function PlayerTile({ player, onSelect }) {
  const [err, setErr] = useState(false);
  const src = player.player_id && !err ? wcPlayerUrl(player.player_id) : null;

  return (
    // paddingTop trick keeps a consistent 3:4 portrait aspect ratio regardless of grid cell size
    <button className="relative overflow-hidden group w-full cursor-pointer active:scale-95 transition-transform"
            style={{ paddingTop: '133%', background: 'rgba(255,255,255,0.04)' }}
            onClick={() => onSelect?.(player)}>
      <div className="absolute inset-0">
        {src ? (
          <img
            src={src}
            alt={player.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            style={{ objectPosition: 'center 15%' }}
            onError={() => setErr(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-white/10 text-5xl select-none">◉</span>
          </div>
        )}

        {/* Bottom gradient */}
        <div className="absolute inset-0 pointer-events-none"
             style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.25) 45%, transparent 72%)' }} />

        {/* Shirt number — top left */}
        <div className="absolute top-1.5 left-1.5 min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center"
             style={{ background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.18)' }}>
          <span className="text-white/90 font-black leading-none" style={{ fontSize: 'clamp(6px, 0.9vw, 9px)' }}>
            {player.shirt || '·'}
          </span>
        </div>

        {/* Name — bottom */}
        <div className="absolute bottom-0 inset-x-0 px-1.5 pb-2 text-center">
          <p className="text-white font-semibold leading-tight line-clamp-2"
             style={{ fontSize: 'clamp(6px, 0.85vw, 10px)', textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
            {player.name}
          </p>
        </div>
      </div>
    </button>
  );
}

// Full squad view for a selected team
function SquadView({ team, onBack }) {
  const [squad,     setSquad]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null); // player clicked → show modal
  const [mapStatus, setMapStatus] = useState(null);  // {mapped, skipped, unmatched}

  useEffect(() => {
    setLoading(true); setSquad(null); setMapStatus(null);
    axios
      .get(`${API_URL}/api/football/hub/team/${team.id}/squad`, { headers: authH() })
      .then(r => {
        setSquad(r.data.squad || null);
        // Fire auto-mapping in background — silently populates StatsAPI ID cache
        axios
          .post(`${API_URL}/api/football/worldcup/team/${team.id}/auto-map-players`, {}, { headers: authH() })
          .then(mr => { if (mr.data?.ok) setMapStatus(mr.data); })
          .catch(() => {});
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [team.id]);

  const sections = squad ? [
    { label: 'GK',  color: '#facc15', players: (squad.goalkeepers  || []).sort((a,b) => +a.shirt - +b.shirt) },
    { label: 'DEF', color: '#4ade80', players: (squad.defenders    || []).sort((a,b) => +a.shirt - +b.shirt) },
    { label: 'MID', color: '#38bdf8', players: (squad.midfielders  || []).sort((a,b) => +a.shirt - +b.shirt) },
    { label: 'FWD', color: '#f87171', players: (squad.forwards     || []).sort((a,b) => +a.shirt - +b.shirt) },
  ].filter(s => s.players.length > 0) : [];

  // Uniform column count — based on the largest group so all tiles are the same width
  const squadCols = squad
    ? Math.max(...sections.map(s => s.players.length))
    : 9;

  return (
    <div className="flex flex-col" style={{ height: '100%', background: '#050b14' }}>
      {/* Header bar */}
      <div className="flex items-center gap-3 px-4 flex-shrink-0"
           style={{ height: 52, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors active:scale-95"
        >
          <span className="text-lg leading-none">←</span>
          <span className="text-sm font-medium">Squads</span>
        </button>
        <div className="w-px h-5 bg-white/10 mx-1 flex-shrink-0" />
        <TeamBadge teamId={team.id} teamName={team.name} size={26} />
        <h2 className="text-white font-bold text-base truncate">{team.name}</h2>
        {squad && (
          <span className="ml-auto text-white/30 text-xs font-mono">
            {Object.values(squad).flat().length} players
          </span>
        )}
      </div>

      {/* Squad grid */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
            <span className="text-white/30 text-sm">Loading squad…</span>
          </div>
        </div>
      ) : !squad || sections.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-white/30 text-sm">
          Squad not available
        </div>
      ) : (
        // Single flat grid — all tiles same width (squadCols columns), position labels span full row
        <div style={{ flex: '1 1 0', minHeight: 0, overflowY: 'auto', padding: '8px 8px 0' }}>
          {sections.map(s => (
            <div key={s.label} style={{ marginBottom: 6 }}>
              {/* Position label row */}
              <div className="flex items-center gap-2 px-1 mb-1.5">
                <span className="text-[10px] font-black uppercase tracking-widest"
                      style={{ color: s.color, textShadow: `0 0 8px ${s.color}` }}>
                  {s.label === 'GK' ? 'Goalkeepers' : s.label === 'DEF' ? 'Defenders' : s.label === 'MID' ? 'Midfielders' : 'Forwards'}
                </span>
                <span className="text-white/20 text-[10px]">— {s.players.length}</span>
              </div>
              {/* Player tiles — same column count for all sections → same tile width */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${squadCols}, 1fr)`,
                gap: 4,
              }}>
                {s.players.map(p => <PlayerTile key={p.player_id || p.name} player={p} onSelect={setSelected} />)}
                {/* Spacer cells so short rows don't stretch to fill */}
                {Array.from({ length: squadCols - s.players.length }).map((_, i) => (
                  <div key={`sp-${i}`} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Player detail modal */}
      {selected && (
        <WcPlayerModal
          player={selected}
          teamId={team.id}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

// ── Tournament Stars panel ────────────────────────────────────────────────────

function _catValue(field, player) {
  const parts = field.split('.');
  let v = player;
  for (const p of parts) v = v?.[p];
  if (v == null) return '—';
  if (field === 'passing.pass_accuracy') return `${Number(v).toFixed(0)}%`;
  if (field === 'rating') return Number(v).toFixed(1);
  return v;
}

const RANK_STYLES = [
  { ring: 'rgba(234,179,8,0.7)',    bg: 'rgba(234,179,8,0.15)',    text: '#facc15' },
  { ring: 'rgba(148,163,184,0.7)',  bg: 'rgba(148,163,184,0.12)', text: '#94a3b8' },
  { ring: 'rgba(217,119,6,0.7)',    bg: 'rgba(217,119,6,0.12)',   text: '#d97706' },
];

function StarsPanel({ onOpenPlayer }) {
  const [leaders, setLeaders]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [activeCat, setActiveCat] = useState(0);
  const intervalRef = useRef(null);
  const pausedRef   = useRef(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/football/worldcup/tournament-leaders`, { headers: authH() })
      .then(r => setLeaders(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const cats = useMemo(
    () => (leaders?.categories || []).filter(c => c.players?.length > 0),
    [leaders]
  );

  useEffect(() => {
    if (!cats.length) return;
    intervalRef.current = setInterval(() => {
      if (!pausedRef.current) setActiveCat(c => (c + 1) % cats.length);
    }, 8000);
    return () => clearInterval(intervalRef.current);
  }, [cats.length]);

  const pause = (ms = 15000) => {
    pausedRef.current = true;
    setTimeout(() => { pausedRef.current = false; }, ms);
  };

  const advance = () => {
    if (!cats.length) return;
    pause();
    setActiveCat(c => (c + 1) % cats.length);
  };

  const goTo = (i) => { pause(); setActiveCat(i); };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-white/60 rounded-full animate-spin" />
      </div>
    );
  }

  if (!cats.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-5 px-8 text-center"
           style={{ background: '#050b14' }}>
        <p className="text-white font-black text-2xl">WC Stars</p>
        <p className="text-white/40 text-base">Stats will appear as matches are played</p>
      </div>
    );
  }

  const cat = cats[activeCat] || cats[0];

  return (
    <div className="h-full flex flex-col select-none" style={{ background: '#050b14' }}
         onClick={advance}>
      {/* Header */}
      <div className="flex-shrink-0 pt-5 pb-3 px-6 text-center">
        <p className="text-white/30 text-[10px] font-bold uppercase tracking-widest mb-1">WC Stars</p>
        <p className="text-white font-black text-2xl leading-tight">{cat.label}</p>
      </div>

      {/* Top-3 player cards */}
      <div className="flex-1 min-h-0 grid grid-cols-3 gap-3 px-4">
        {cat.players.slice(0, 3).map((p, i) => {
          const src = p.pa_player_id ? wcPlayerUrl(p.pa_player_id) : null;
          const rank = RANK_STYLES[i] || RANK_STYLES[2];
          const displayVal = _catValue(cat.field, p);
          return (
            <button key={p.statsapi_player_id || i}
              onClick={e => {
                e.stopPropagation();
                onOpenPlayer?.({ player_id: p.pa_player_id, player: p.player_name, team_id: p.pa_team_id });
              }}
              className="flex flex-col rounded-2xl overflow-hidden text-left cursor-pointer w-full h-full transition-opacity active:opacity-70"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              {/* Image */}
              <div className="relative flex-1 min-h-0 overflow-hidden" style={{ background: '#0d1421' }}>
                {src && (
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url(${src})`,
                    backgroundSize: 'auto 100%',
                    backgroundPosition: 'center top',
                    backgroundRepeat: 'no-repeat',
                  }} />
                )}
                <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, #050b14 0%, rgba(5,11,20,0.3) 60%, transparent 100%)' }} />
                {/* Rank badge */}
                <div className="absolute top-2.5 left-2.5 w-8 h-8 rounded-full flex items-center justify-center font-black text-sm"
                  style={{ background: rank.bg, border: `2px solid ${rank.ring}` }}>
                  <span style={{ color: rank.text }}>{i + 1}</span>
                </div>
                {/* Stat value */}
                <div className="absolute bottom-2.5 right-2.5 text-right">
                  <p className="text-white font-black text-4xl tabular-nums leading-none">{displayVal}</p>
                  <p className="text-white/40 text-[10px] uppercase tracking-widest mt-0.5">{cat.label}</p>
                </div>
              </div>
              {/* Player strip */}
              <div className="px-3 py-2.5 flex-shrink-0" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-white font-bold text-sm truncate leading-tight">{p.player_name}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {p.pa_team_id && <TeamBadge teamId={p.pa_team_id} size={14} />}
                  <span className="text-white/35 text-xs tabular-nums">{p.appearances} app{p.appearances !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Dot pagination + swipe hint */}
      <div className="flex-shrink-0 pb-4 pt-3 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2">
          {cats.map((_, i) => (
            <button key={i}
              onClick={e => { e.stopPropagation(); goTo(i); }}
              className="rounded-full transition-all duration-300"
              style={{
                width:  i === activeCat ? 20 : 6,
                height: 6,
                background: i === activeCat ? 'white' : 'rgba(255,255,255,0.2)',
              }}
            />
          ))}
        </div>
        <p className="text-white/20 text-[10px] font-medium uppercase tracking-widest">
          Tap to browse · {activeCat + 1} of {cats.length}
        </p>
      </div>
    </div>
  );
}

// Squads tab — team picker grid → squad view
function SquadsPanel({ groups, matches, bracket }) {
  const [selectedTeam, setSelectedTeam] = useState(null);

  // Reuse same placeholder + team-building logic as TeamPickerModal
  const isPlaceholder = (name) =>
    !name ||
    /^(Winner|Loser|Runner-up|Group [A-Z]\/)/i.test(name) ||
    /^(Winner|Loser|Runner-up)\s+(Group|Match)/i.test(name);

  const groupedTeams = useMemo(() => {
    if (groups?.groups?.length) {
      const real = groups.groups
        .filter(g => /^Group [A-L]$/i.test(g.name))
        .map(g => ({
          groupName: g.name,
          teams: (g.rows || [])
            .filter(r => r.team_id && !isPlaceholder(r.team))
            .map(r => ({ id: String(r.team_id), name: r.team })),
        }))
        .filter(g => g.teams.length > 0);
      if (real.length > 0) return real;
    }
    const seen = new Set(); const all = [];
    (matches || []).forEach(m => {
      if (m.home_team_id && !seen.has(m.home_team_id) && !isPlaceholder(m.home_team_name)) {
        seen.add(m.home_team_id); all.push({ id: m.home_team_id, name: m.home_team_name });
      }
      if (m.away_team_id && !seen.has(m.away_team_id) && !isPlaceholder(m.away_team_name)) {
        seen.add(m.away_team_id); all.push({ id: m.away_team_id, name: m.away_team_name });
      }
    });
    all.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [{ groupName: null, teams: all }];
  }, [groups, matches]); // eslint-disable-line react-hooks/exhaustive-deps

  // Work out which teams are STILL IN, from the resolved knockout bracket (winners
  // are advanced server-side, incl. penalty shootouts). We compute "still in"
  // rather than "out" because the bracket is built from a rolling fixtures window:
  // early rounds age out as the tournament progresses, so past rounds are NOT a
  // reliable record — but a team's current/next match always is. A team is still
  // in if it has an upcoming (not-finished) knockout match, or it WON its latest
  // knockout tie and hasn't since lost. Everyone else in the tournament is out.
  // The tie winner is found by whether it advances to a later round; scoreline is
  // the fallback. The 3rd-place play-off (rank 5) isn't advancement, so excluded.
  const elim = useMemo(() => {
    const rounds = bracket || [];
    const realId = (m, side) => {
      const id = m[`${side}_team_id`];
      return id && !isPlaceholder(m[`${side}_team_name`]) ? String(id) : null;
    };
    const ADV = { 1: 1, 2: 2, 3: 3, 4: 4, 6: 5 }; // rank 5 (3rd place) excluded
    const maxAdv = {};
    rounds.forEach(r => (r.matches || []).forEach(m => {
      [realId(m, 'home'), realId(m, 'away')].forEach(id => {
        if (!id) return;
        const lvl = ADV[r.rank] || 0;
        if (lvl > (maxAdv[id] || 0)) maxAdv[id] = lvl;
      });
    }));
    const losers = new Set();     // lost a finished tie
    const winners = new Set();    // won a finished tie
    const upcoming = new Set();   // in a not-yet-finished tie
    let koSeeded = false;
    rounds.forEach(r => (r.matches || []).forEach(m => {
      const h = realId(m, 'home'), a = realId(m, 'away');
      if (h || a) koSeeded = true;
      if (m.status !== 'finished') {
        if (h) upcoming.add(h);
        if (a) upcoming.add(a);
        return;
      }
      if (!h || !a) return;
      const L = ADV[r.rank] || 0;
      const hs = m.home_score, as = m.away_score;
      let winner = null;
      if (L && (maxAdv[h] || 0) > L && (maxAdv[a] || 0) <= L) winner = h;
      else if (L && (maxAdv[a] || 0) > L && (maxAdv[h] || 0) <= L) winner = a;
      else if (hs != null && as != null && hs !== as) winner = hs > as ? h : a;
      if (winner) { winners.add(winner); losers.add(winner === h ? a : h); }
    }));
    // Still in = has an upcoming tie, or won its last tie — and hasn't lost since.
    const stillIn = new Set();
    upcoming.forEach(id => { if (!losers.has(id)) stillIn.add(id); });
    winners.forEach(id => { if (!losers.has(id)) stillIn.add(id); });
    return { stillIn, koSeeded };
  }, [bracket]);

  const allTeams = useMemo(() => {
    const seen = new Set();
    const { stillIn, koSeeded } = elim;
    return groupedTeams
      .flatMap(g => g.teams)
      .filter(t => { if (seen.has(t.id)) return false; seen.add(t.id); return true; })
      // Once the knockouts are underway, any team not still in is out (this covers
      // both knockout losers and group-stage non-qualifiers). Before then, no one
      // is marked out.
      .map(t => ({ ...t, out: koSeeded && !stillIn.has(t.id) }))
      // Still-in teams first, then those knocked out — each block alphabetical
      .sort((a, b) =>
        a.out !== b.out ? (a.out ? 1 : -1) : (a.name || '').localeCompare(b.name || ''));
  }, [groupedTeams, elim]);

  const outCount = useMemo(() => allTeams.filter(t => t.out).length, [allTeams]);

  const cols = useMemo(() => {
    const n = allTeams.length; if (n === 0) return 12;
    let best = 12, bestDiff = Infinity;
    for (let c = 8; c <= 16; c++) {
      const r = Math.ceil(n / c);
      const diff = Math.abs(c / r - 3);
      if (diff < bestDiff) { bestDiff = diff; best = c; }
    }
    return best;
  }, [allTeams.length]);
  const rows = Math.ceil(allTeams.length / cols);

  if (selectedTeam) return <SquadView team={selectedTeam} onBack={() => setSelectedTeam(null)} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#050b14' }}>
      {/* Ambient glows */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
        <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px]"
             style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.15), transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px]"
             style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.12), transparent 70%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex items-center justify-between px-6 pt-5 pb-3 flex-shrink-0">
        <div>
          <p className="text-white/30 text-[10px] font-bold uppercase tracking-[0.25em] mb-0.5">WC 2026</p>
          <h2 className="text-white text-xl font-black tracking-tight">Squads</h2>
        </div>
        <span className="text-white/20 text-xs">
          {allTeams.length} teams
          {outCount > 0 && (
            <span className="ml-2" style={{ color: 'rgba(248,113,113,0.6)' }}>· {outCount} out</span>
          )}
        </span>
      </div>

      {/* Team badge grid */}
      <div className="relative z-10 px-4 pb-4" style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}>
        <div style={{
          display: 'grid', width: '100%', height: '100%',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridTemplateRows: `repeat(${rows}, 1fr)`,
          gap: '6px',
        }}>
          {allTeams.map(team => (
            <button
              key={team.id}
              onClick={() => setSelectedTeam(team)}
              title={team.out ? `${team.name} — out of the tournament` : team.name}
              className="relative flex flex-col items-center justify-center rounded-2xl transition-all duration-200 active:scale-95 group overflow-hidden"
              style={{
                // Out teams get a discreet red-tinted, dimmed treatment
                background: team.out ? 'rgba(239,68,68,0.05)' : 'rgba(255,255,255,0.04)',
                border: team.out ? '1.5px solid rgba(239,68,68,0.28)' : '1.5px solid rgba(255,255,255,0.07)',
                opacity: team.out ? 0.6 : 1,
              }}
            >
              <div className="transition-transform duration-200 group-hover:scale-110 flex items-center justify-center"
                   style={{ height: '55%', filter: team.out ? 'grayscale(0.7)' : 'none' }}>
                <img src={badgeUrl(team.id)} alt={team.name}
                     className="h-full w-full object-contain"
                     onError={e => { e.target.style.display='none'; }} />
              </div>
              <span className="px-1 text-center font-semibold leading-tight line-clamp-2 mt-1"
                    style={{ fontSize: 'clamp(8px, 1vw, 11px)',
                             color: team.out ? 'rgba(248,113,113,0.8)' : 'rgba(255,255,255,0.6)',
                             maxWidth: '90%' }}>
                {team.name}
              </span>
              {team.out && (
                <span className="absolute top-1 right-1 rounded px-1 font-bold uppercase tracking-wide"
                      style={{ fontSize: '7px', color: 'rgba(248,113,113,0.85)',
                               background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  Out
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── tab config ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'home',    label: 'Home' },
  { id: 'groups',  label: 'Groups' },
  { id: 'bracket', label: 'Bracket' },
  { id: 'squads',  label: 'Squads' },
  { id: 'scorers', label: 'Top Scorers' },
  { id: 'stars',   label: 'WC Stars' },
  { id: 'stories', label: 'Stories' },
];

// ── main page ─────────────────────────────────────────────────────────────────

export default function WorldCupDisplayPage() {
  const [activeTab, setActiveTab] = useState('home');

  const [matches,        setMatches]        = useState([]);
  const [liveMatches,    setLiveMatches]    = useState([]);
  const [groups,         setGroups]         = useState(null);
  const [scorers,        setScorers]        = useState([]);
  const [scorersFetched, setScorersFetched] = useState(false); // guard against empty-result loop
  const [stories,        setStories]        = useState(null);
  const [bracket,        setBracket]        = useState([]);

  const [displayCompId, setDisplayCompId] = useState(WC_COMP_ID);
  const [displayTitle,  setDisplayTitle]  = useState('FIFA World Cup 2026');
  const [statsData,     setStatsData]     = useState(null);

  const [openStory,     setOpenStory]     = useState(null);
  const [openPlayer,    setOpenPlayer]    = useState(null); // { player_id, player }
  // player_id → team name enriched from profiles
  const [profileTeams,     setProfileTeams]     = useState({});
  const [profilesFetched,  setProfilesFetched]  = useState(false);

  // Tracks the resolved comp ID so polling intervals always use the latest value
  const compIdRef = useRef(WC_COMP_ID);

  // ── fetchers ──────────────────────────────────────────────────────────────

  const fetchMatches = useCallback(async (compId) => {
    try {
      const r = await axios.get(`${API_URL}/api/football/hub/fixtures`,
        { params: { comp_id: compId ?? compIdRef.current }, headers: authH() });
      setMatches(r.data.matches || []);
    } catch {}
  }, []);

  const fetchLive = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/football/worldcup/live`, { headers: authH() });
      setLiveMatches(r.data.live_matches || []);
    } catch {}
  }, []);

  const fetchGroups = useCallback(async (compId) => {
    const resolvedCompId = compId ?? compIdRef.current;
    try {
      const r = await axios.get(`${API_URL}/api/football/hub/table`,
        { params: { comp_id: resolvedCompId }, headers: authH() });
      setGroups(r.data);
      // Populate the WC team registry so TeamPage / MatchViewModal use WC images.
      // This page can be repointed at a different competition via site settings
      // (e.g. showing the Premier League here instead), so only register teams
      // when we're actually displaying the World Cup — otherwise every club from
      // whatever competition is configured gets treated as a WC team everywhere.
      if (resolvedCompId === WC_COMP_ID) {
        const teamIds = (r.data?.groups || []).flatMap(g => (g.rows || []).map(row => row.team_id)).filter(Boolean);
        if (teamIds.length) setWcTeamIds(teamIds);
      }
    } catch {}
  }, []);

  const fetchScorers = useCallback(async (compId) => {
    setScorersFetched(true); // set immediately so the effect never fires a second time
    try {
      const r = await axios.get(`${API_URL}/api/football/hub/top-scorers`,
        { params: { comp_id: compId ?? compIdRef.current }, headers: authH() });
      const list = r.data.scorers || [];
      setScorers(list);

      // Batch-fetch player profiles to get correct team names
      // Profiles are cached server-side for 1h so this is cheap
      const needTeam = list.filter(s => s.player_id && !s.team);
      if (needTeam.length > 0) {
        const results = await Promise.allSettled(
          needTeam.map(s =>
            axios.get(`${API_URL}/api/football/hub/player/${s.player_id}`, { headers: authH() })
          )
        );
        const teams = {};
        results.forEach((res, i) => {
          if (res.status === 'fulfilled') {
            const club = res.value?.data?.clubs?.[0];
            if (club?.name) teams[needTeam[i].player_id] = { name: club.name, team_id: club.team_id };
          }
        });
        if (Object.keys(teams).length > 0) setProfileTeams(teams);
      }
      setProfilesFetched(true);
    } catch {}
  }, []);

  const fetchStats = useCallback(async (compId) => {
    try {
      const r = await axios.get(`${API_URL}/api/football/display/stats`, {
        params: { pa_comp_id: compId ?? compIdRef.current }, headers: authH()
      });
      setStatsData(r.data);
    } catch { /* non-critical */ }
  }, []);

  const fetchBracket = useCallback(async (compId) => {
    try {
      const r = await axios.get(`${API_URL}/api/football/hub/bracket`, {
        params: { comp_id: compId ?? compIdRef.current }, headers: authH()
      });
      setBracket(r.data.rounds || []);
    } catch { /* non-critical */ }
  }, []);

  const fetchStories = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/football/hub/news`, { headers: authH() });
      const allItems = r.data?.items || [];
      // Keep only stories whose PA subject tags contain "world cup" or "england"
      // (case-insensitive contains so it matches however PA or Podium phrase it:
      //  "FIFA World Cup", "2026 World Cup", "World Cup 2026", "England" etc.)
      const filtered = allItems.filter(item => {
        const subjects = item.subject || [];
        return subjects.some(s => {
          const text = `${s.name || ''} ${s.code || ''}`.toLowerCase();
          return text.includes('world cup') || text.includes('england');
        });
      });
      setStories({ ...r.data, items: filtered });
    } catch {}
  }, []);

  useEffect(() => {
    // Load settings first so all fetches use the correct comp ID
    (async () => {
      let compId = WC_COMP_ID;
      try {
        const sr = await axios.get(`${API_URL}/api/site-settings/public`, { headers: authH() });
        compId = sr.data.display_comp_id || WC_COMP_ID;
        const title = sr.data.display_title || 'FIFA World Cup 2026';
        compIdRef.current = compId;
        setDisplayCompId(compId);
        setDisplayTitle(title);
      } catch { /* non-critical — fall back to WC_COMP_ID */ }

      // Fetch fixtures + live + stories + stats + scorers with the resolved comp ID
      fetchMatches(compId);
      fetchLive();
      fetchStories();
      fetchStats(compId);
      fetchScorers(compId);
    })();

    // Polling so the kiosk never goes stale without a manual refresh:
    //  live every 30 s · fixtures 60 s · stories 5 min · stats 2 min · scorers 5 min
    const t1 = setInterval(() => { fetchLive(); }, 30000);
    const t1b = setInterval(() => { fetchMatches(); }, 60000);
    const t2 = setInterval(fetchStories, 300000);
    const t3 = setInterval(() => { fetchStats(); }, 120000);
    const t4 = setInterval(() => { fetchScorers(); }, 300000);
    return () => { clearInterval(t1); clearInterval(t1b); clearInterval(t2); clearInterval(t3); clearInterval(t4); };
  }, [fetchMatches, fetchLive, fetchStories, fetchStats, fetchScorers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'groups'  && !groups)              fetchGroups(displayCompId);
    if (activeTab === 'squads'  && !groups)              fetchGroups(displayCompId); // need groups for team picker
    if (activeTab === 'scorers' && !scorersFetched)      fetchScorers(displayCompId);
    // Always have table data ready for scorer team enrichment
    if (activeTab === 'scorers' && !groups)              fetchGroups(displayCompId);
    // Refetch the bracket when the bracket or squads tab opens so scores,
    // advances and team elimination status stay fresh
    if (activeTab === 'bracket' || activeTab === 'squads') fetchBracket(displayCompId);
  }, [activeTab, groups, scorersFetched, fetchGroups, fetchScorers, fetchBracket, displayCompId]);

  // ── inactivity: 60 s on sub-tab → home ───────────────────────────────────

  const timerRef = useRef(null);
  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setActiveTab('home'), 60000);
  }, []);

  useEffect(() => {
    if (activeTab === 'home') { clearTimeout(timerRef.current); return; }
    const evts = ['touchstart', 'mousedown', 'mousemove', 'keydown', 'scroll', 'click'];
    evts.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    resetTimer();
    return () => {
      evts.forEach(e => window.removeEventListener(e, resetTimer));
      clearTimeout(timerRef.current);
    };
  }, [activeTab, resetTimer]);

  // ── kiosk: block all external navigation ─────────────────────────────────
  // Prevents users on the touchscreen from tapping links and leaving the page.
  // Runs on capture phase so it intercepts before any element handler fires.
  useEffect(() => {
    const block = (e) => {
      const a = e.target.closest('a[href]');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('http') || href.startsWith('//') || href.startsWith('www')) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('click',      block, true);
    document.addEventListener('touchstart', block, { capture: true, passive: false });
    // Also block window.open (Twitter widget uses it for "open tweet" actions)
    const origOpen = window.open;
    window.open = () => null;
    return () => {
      document.removeEventListener('click',      block, true);
      document.removeEventListener('touchstart', block, { capture: true, passive: false });
      window.open = origOpen;
    };
  }, []);

  const navigate = useNavigate();

  // Reset to this main screen after 5 min inactivity — keeps the kiosk display fresh
  useKioskInactivity({ enabled: true });

  const openMatchHandler = useCallback((m) => {
    // Navigate to the kiosk-specific route so AppLayout (and its nav bar) never appears
    navigate(`/wc2026/match/${m.pa_match_id}`);
  }, [navigate]);

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-screen w-screen overflow-hidden bg-background">

      {/* ── combined header + nav (single row) ── */}
      <header className="flex items-center gap-4 px-4 py-2 border-b border-border
                         bg-background flex-shrink-0 overflow-x-auto">
        {/* Logos */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <img src={PODIUM_LOGO} alt="Podium" className="h-7 object-contain" />
          <span className="text-muted-foreground/40 text-sm font-light">×</span>
          <img src={WC_LOGO} alt="FIFA World Cup 2026" className="h-8 w-8 object-contain" />
        </div>

        {/* Tab buttons */}
        <nav className="flex items-center gap-1 flex-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all
                ${activeTab === t.id
                  ? 'bg-primary/15 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-foreground border border-transparent hover:bg-secondary'
                }`}>
              {t.label}
            </button>
          ))}
          {/* WC Stats link — navigates to dedicated stats page */}
          <button
            onClick={() => navigate('/wc2026/stats')}
            className="px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all
              text-amber-400 border border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/20"
          >
            WC Stats
          </button>
        </nav>

        {/* Right: live badge */}
        {liveMatches.length > 0 && (
          <div className="flex items-center gap-1.5 bg-green-500/10 border border-green-500/30
                          px-3 py-1 rounded-full flex-shrink-0">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-green-400 text-xs font-bold">{liveMatches.length} LIVE</span>
          </div>
        )}
      </header>

      {/* ── panel ── */}
      <main className="flex-1 overflow-hidden">
        {activeTab === 'home' && (
          <HomePanel matches={matches} liveMatches={liveMatches} stories={stories}
            onOpenMatch={openMatchHandler} onOpenStory={setOpenStory}
            statsData={statsData} displayTitle={displayTitle} groups={groups} />
        )}
        {activeTab === 'groups'  && <GroupsPanel groups={groups} />}
        {activeTab === 'bracket' && <BracketPanel rounds={bracket} />}
        {activeTab === 'squads'  && <SquadsPanel groups={groups} matches={matches} bracket={bracket} />}
        {activeTab === 'scorers' && (
          <ScorersPanel scorers={scorers} matches={matches} groups={groups}
            onOpenPlayer={setOpenPlayer} isWc={Number(displayCompId) === WC_COMP_ID} />
        )}
        {activeTab === 'stars'   && <StarsPanel onOpenPlayer={setOpenPlayer} />}
        {activeTab === 'stories' && <StoriesPanel stories={stories} onOpen={setOpenStory} />}
      </main>

      {openStory && <StoryReader story={openStory} onClose={() => setOpenStory(null)} />}
      {openPlayer && (
        <PlayerProfileModal
          playerId={openPlayer.player_id}
          playerName={openPlayer.player}
          teamId={openPlayer.team_id}
          onClose={() => setOpenPlayer(null)}
        />
      )}
    </div>
  );
}
